import React, { useState } from 'react';

const RealisticSmokingConfigsContent = ({ configCode, itemsCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'items'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Realistic Smoking Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Realistic Smoking script offers extensive configuration options for a highly customizable experience. Key aspects include:</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Framework Export Configuration:</b> Automatic detection for <code>ESX</code> or <code>QB/QBX</code>, with the ability to override the default exports.</li>
        <li><b>General Settings:</b> Set the position of the options menu, enable Ox Inventory durability system, configure command to take the ear item, disable combat buttons, and toggle sound effects.</li>
        <li><b>Rolling Joints Minigame:</b> Enable or disable the rolling minigame, with configurable difficulty levels (<code>easy</code>, <code>medium</code>, <code>hard</code>), area size, speed multiplier, and required key inputs.</li>
        <li><b>High Effect System:</b> Customize the maximum high value, visual effect name (e.g. <code>spectator9</code>), camera shake during aiming, sprint disablement, periodic high removal settings, and optional high-related movement animation.</li>
        <li><b>Button Mappings:</b> Keybind customization for all major actions: Use (<code>LEFT MOUSE</code>), Accept, Light, Charge (<code>PAGEUP</code>), Options (<code>R</code>), Throw/Back/Hide (<code>X</code>).</li>
        <li><b>Status Effects:</b> Framework-specific triggers for applying or removing <code>ARMOR</code>, <code>STRESS</code>, and <code>HIGH</code> values after smoking, including built-in support for <code>esx_status</code> and <code>QB-core</code>.</li>
        <li><b>Notification Handling:</b> Easily replace default <code>ESX.ShowNotification</code> or <code>QBCore.Functions.Notify</code> with your own notification systems using a unified wrapper function.</li>
        <li><b>Custom Draw Menu:</b> Override the default smoke options menu with your own export or trigger by customizing the <code>Config.CustomMenu</code> function.</li>
        <li><b>Menu UI Configuration:</b> Modify text font, scale, position, color, and background settings for the <code>Main</code>, <code>Status</code>, and <code>Options</code> on-screen menus.</li>
        <li><b>Localization & Translations:</b> Full support for custom text and labels shown in the UI menus, notifications, and interaction prompts.</li>
      </ul>
      <p className="text-gray-300 mb-4">This level of customization ensures the script integrates seamlessly with your server’s gameplay and UI style.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
        <button
          onClick={() => setActiveTab('items')}
          className={`px-4 py-2 rounded-r-lg font-semibold transition ${activeTab === 'items'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Items
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {activeTab === 'config'? configCode : itemsCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const BankingSystemConfigsContent = ({ configCode, logsConfigCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'serverConfig'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Banking System Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Banking System script offers extensive configuration options, including multi-language UI, flexible banking access methods, comprehensive transaction logging, and detailed loan system settings.</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Localization & UI Language:</b> Set the default language and define available languages for the user interface (e.g., English, Czech, German).</li>
        <li><b>Abuse Prevention:</b> Configure a custom kick message for players attempting to abuse the banking system.</li>
        <li><b>Banking Interface Access:</b> Choose the method for opening the banking UI, with support for `ox_target`, `okok_textui`, `ox_lib` text UI, `esx` text UI, and `cd` draw text UI.</li>
        <li><b>Notification System Integration:</b> Select your preferred notification system from options like `okok`, `ox_lib`, `esx`, `cd`, `mythic`, or `pnotify`.</li>
        <li><b>Debug Mode:</b> Enable or disable a debug mode for troubleshooting and development.</li>
        <li><b>Customizable Transaction Labels:</b> Define display labels for various transaction types, including account transfers, deposits, withdrawals, and savings movements.</li>
        <li><b>Loan System Parameters:</b> Configure detailed settings for loans, such as maximum active loans, minimum and maximum loan amounts, loan terms (in days), interest rates, payment intervals, grace periods, and minimum player playtime for eligibility.</li>
        <li><b>Cash Item Definition:</b> Specify the item name used for physical cash within the game.</li>
        <li><b>ATM Model Recognition:</b> Provide a list of prop models that the script should recognize as ATMs for interaction.</li>
        <li><b>Bank Zone Definitions:</b> Define specific locations and dimensions for bank interaction zones using coordinates, heading, length, width, and height boundaries.</li>
        <li><b>Job Society Account Integration:</b> Map in-game job names to their corresponding society accounts for banking operations (e.g., `ambulance` to `society_ambulance`).</li>
        <li><b>Discord Logging:</b> Enable or disable comprehensive Discord logging for a wide range of banking events, including transactions, account creation, card management, loan payments, and society banking actions. Each log type allows for custom webhooks, colors, titles, and descriptions.</li>
      </ul>
      <p className="text-gray-300 mb-4">This level of detail allows server owners to fine-tune every aspect of the banking experience.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
        <button
          onClick={() => setActiveTab('logsConfig')}
          className={`px-4 py-2 rounded-r-lg font-semibold transition ${activeTab === 'logsConfig'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Logs Config
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {activeTab === 'config'? configCode : logsConfigCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const RealisticNeedsConfigsContent = ({ configCode, itemsCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'serverConfig'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Realistic Needs Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Realistic Needs script offers extensive configuration options to fine-tune player needs, status effects, and the integrated drunk system, allowing for a highly customizable and immersive gameplay experience.</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Framework Export Configuration:</b> Automatic detection for ESX, QB, or QBX, with options to replace default exports.</li>
        <li><b>General Settings:</b> Control saving to database, and automatic item placement.</li>
        <li><b>Button Mappings:</b> Customize keybinds for consumption, placing items, hiding menus, and accepting actions.</li>
        <li><b>Notification Function:</b> Customizable notification system, supporting default ESX/QB notifies or custom exports.</li>
        <li><b>Custom Menu Function:</b> Option to replace the default needs draw menu with a custom one.</li>
        <li><b>Status Effects:</b> Settings for adding food, drink, alcohol, and removing stress, with framework-specific triggers.</li>
        <li><b>Drunk System:</b> Extensive configuration for the drunk system, including blood alcohol limit (MaxDrunk), effect update time, alcohol removal rate, camera vibration while aiming, visual effects (e.g., 'spectator5'), and dynamic movement changes based on alcohol level (Phases).</li>
        <li><b>Consume Items:</b> Configuration for various consumable items, including their props, bone indexes for holding animations, and effects on player stats.</li>
      </ul>
      <p className="text-gray-300 mb-4">This comprehensive configuration ensures flexibility in managing player needs and effects.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
        <button
          onClick={() => setActiveTab('itemsCode')}
          className={`px-4 py-2 rounded-r-lg font-semibold transition ${activeTab === 'itemsCode'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Items
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {activeTab === 'config'? configCode : itemsCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const MileageMeterConfigsContent = ({ configCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config' or 'serverConfig'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Mileage Meter Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Mileage Meter script offers extensive configuration options to control vehicle mileage tracking, manipulation mechanics, visual feedback, and integration with your server's framework and UI.</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Framework Integration:</b> Configurable ESX export for framework compatibility.</li>
        <li><b>Mileage Manipulation Items:</b> Define item names for "twisting" (reducing) mileage (`twist_mil`) and "decrypting" mileage (`mil_decrypter`).</li>
        <li><b>Failure Consequences:</b> Toggle whether the vehicle catches fire upon unsuccessful mileage decryption or reduction attempts.</li>
        <li><b>Mileage Display:</b> Choose between automatic 2D menu display when entering an owned vehicle or manual display via a configurable button.</li>
        <li><b>Vehicle Wear & Dirt:</b> Enable or disable dynamic vehicle performance decrease and visual dirt accumulation based on accumulated mileage, with configurable thresholds.</li>
        <li><b>Mileage Mini-Game:</b> Configure the mini-game for mileage manipulation, including difficulty (easy, medium, hard), area size, speed multiplier, and required button inputs.</li>
        <li><b>Button Mappings:</b> Customize the control ID for displaying the mileage menu.</li>
        <li><b>Menu UI Customization:</b> Adjust the font, color, scale, position, and background of the main 2D mileage display menu.</li>
        <li><b>Notification Function:</b> Customizable notification system, typically using `ESX.ShowNotification` for in-game messages.</li>
        <li><b>Vehicle Performance Adjustment:</b> Detailed function to define how vehicle performance and dirt levels change at specific mileage thresholds (e.g., 100, 150, 200, 300, 400, 500, 600+ miles).</li>
      </ul>
      <p className="text-gray-300 mb-4">This comprehensive configuration ensures precise control over the mileage system's behavior and integration.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {configCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const WaterGunConfigsContent = ({ configCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Water Gun Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Water Gun script offers extensive configuration options to customize its core mechanics, user interface, and integration with your server's framework, allowing for a dynamic and immersive water-based gameplay experience.</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Framework Integration:</b> Automatic detection for ESX, QB, QBX, or STANDALONE setups, with configurable export functions.</li>
        <li><b>UI Display Mode:</b> Control whether the in-game menu shows interactive buttons or only the water tank and pumping status.</li>
        <li><b>Button Mappings:</b> Customize keybinds for Splash, Reload, Pump, and Hide actions.</li>
        <li><b>Water Gun Item Properties:</b> Define properties for water gun items by their item name, including maximum water capacity, and the min/max ranges for pumping and reloading pressure.</li>
        <li><b>Notification Function:</b> Customizable notification system integration.</li>
      </ul>
      <p className="text-gray-300 mb-4">This comprehensive configuration ensures precise control over the water gun system's behavior and integration.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {configCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const MultijobConfigsContent = ({ configCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Multijob System Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Devcore Multijob script offers extensive configuration options to manage simultaneous jobs, customize the user interface, integrate with various frameworks, and fine-tune administrative controls for a dynamic roleplay experience.</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>General System Settings:</b>
          <ul>
            <li>Config.MaxJobs: Set the maximum number of jobs a player can hold simultaneously (default: 3)</li>
            <li>Config.OpenMenuKey: Configure the keyboard shortcut to open the Multijob menu (e.g., 'F6')</li>
            <li>Config.MenuCommand: Define the chat command to open the Multijob menu (e.g., `/jobs`)</li>
            <li>Config.Side: Set the UI display side (e.g., "right")</li>
          </ul>
        </li>
        <li><b>Framework & Dependencies:</b>
          <ul>
            <li><b>Framework Compatibility:</b> Automatic detection and compatibility with ESX, QB, and QBX frameworks</li>
            <li><b>Required Dependencies:</b> Requires `ox_lib` and `oxmysql`</li>
            <li><b>Server Artifacts:</b> Requires server artifacts version 5181 or above</li>
          </ul>
        </li>
        <li><b>User Interface (UI) Customization:</b>
          <ul>
            <li><b>UI Appearance:</b> Fully customizable UI appearance with modern, responsive design and smooth animations</li>
            <li><b>UI Elements:</b> Includes tooltip hints and a footer with useful tips</li>
            <li><b>Localization:</b> Complete localization support for multiple languages</li>
          </ul>
        </li>
        <li><b>Job Management & Display:</b>
          <ul>
            <li><b>Simultaneous Jobs:</b> Supports up to 3 simultaneous jobs with automatic rotation</li>
            <li><b>Job Information Display:</b> Displays payout amount, number of online employees, and time spent for each job</li>
            <li><b>Job Grade Maintenance:</b> Maintains job grades when switching between jobs</li>
            <li><b>Default & Custom Jobs:</b> Includes default jobs (Car dealer, Police, EMS, Taxi) with easy configurability to add more</li>
            <li><b>Ignored Jobs:</b> Define jobs that should not be managed by the Multijob system (e.g., `unemployed`)</li>
            <li><b>Duty Restrictions:</b> Configure jobs that deny duty when combined with other jobs (e.g., `ambulance`, `police`)</li>
            <li><b>Whitelisted Jobs:</b> Explicitly whitelist jobs for Multijob functionality</li>
          </ul>
        </li>
        <li><b>Notifications & Keybinds:</b>
          <ul>
            <li><b>Notification System:</b> Multiple notification system support (`ox_lib`, ESX, custom)</li>
            <li><b>Sound Effects & Keybinds:</b> Configurable sound effects and keybinds</li>
          </ul>
        </li>
        <li><b>Performance & Data:</b>
          <ul>
            <li><b>Optimized Performance:</b> Minimal resource impact, 0 ms when inactive</li>
            <li><b>Data Persistence:</b> Automatic job saving, conflict prevention, and persistence storage with reliable MySQL async operations</li>
          </ul>
        </li>
        <li><b>Administrative & Debugging:</b>
          <ul>
            <li><b>Admin Commands:</b> Commands for job management and player monitoring</li>
            <li><b>Debug Mode:</b> Debug mode and export functions for integration</li>
          </ul>
        </li>
      </ul>
      <p className="text-gray-300 mb-4">This comprehensive configuration ensures precise control over the multijob system's behavior and integration, allowing for a highly tailored and engaging server experience.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {configCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const TagSystemConfigsContent = ({ configCode }) => {
  const [activeTab, setActiveTab] = useState('config'); // 'config'

  return (
    <div>
      <h4 className="text-xl font-semibold mb-2 text-devcore.primary">Tag System Configuration Overview</h4>
      <p className="text-gray-300 mb-4">The Devcore Tag System script offers extensive configuration options to control tag creation, display, permissions, and integration with your server's framework and notification system, providing a dynamic visual labeling experience.</p>
      <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Framework Integration & Detection:</b> Automatic detection for `ESX`, `QB`, `QBX`, or `STANDALONE` setups. [1]</li>
        <li><b>Notification System:</b> Customizable notification function, supporting `ESX`, `QB`/`QBX`, and custom exports for standalone setups. [1]</li>
        <li><b>Command Control:</b> Enable or disable the tag command, and define its name (e.g., `Config.TagCommand = 'tag'`). [1]</li>
        <li><b>Permissions System:</b> Integrates with `ESX` group system or exports, and `QBCore`/`QBX`/`Standalone` using ACE permissions in `server.cfg` or exports. [1]</li>
        <li><b>UI Display:</b> Tags are displayed in 3D space for immersive visual labeling. [2]</li>
        <li><b>Synchronization:</b> All tag displays are synchronized with other players for a consistent experience. [2]</li>
        <li><b>Performance:</b> The script is optimized for efficient performance. [2]</li>
        <li><b>Server Requirements:</b> Requires server artifacts version 5181 or above. [2]</li>
      </ul>
      <p className="text-gray-300 mb-4">This comprehensive configuration ensures precise control over the tag system's behavior and integration within your server environment.</p>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 rounded-l-lg font-semibold transition ${activeTab === 'config'? 'bg-devcore.primary text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          Config
        </button>
      </div>

      <div className="bg-devcore.dark p-4 rounded-lg mb-4">
        <div className="flex space-x-2 mb-3">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <pre className="font-mono text-sm text-gray-300 overflow-x-auto">
          <code>
            {configCode}
          </code>
        </pre>
      </div>
    </div>
  );
};

const DocsPage = () => {
  const [openCategories, setOpenCategories] = useState({});
  const [currentContentKey, setCurrentContentKey] = useState('gettingStarted');

  const toggleCategory = (key) => {
    setOpenCategories(prev => ({
    ...prev,
      [key]:!prev[key] // Toggle the boolean value for the specific key
    }));
  };

  const docsData = [
    {
      category: "Scripts",
      items: [
        {
          name: "Realistic Smoking",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/realistic-smoking", contentKey: "realisticSmokingGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/realistic-smoking/installation", contentKey: "realisticSmokingInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/realistic-smoking/configs", contentKey: "realisticSmokingConfigs" },
            { title: "Exports", url: "https://docs.devcore.cz/resources/realistic-smoking/exports", contentKey: "realisticSmokingExports" }
          ]
        },
        {
          name: "Banking System",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/banking-system", contentKey: "bankingSystemGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/banking-system/installation", contentKey: "bankingSystemInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/banking-system/configs", contentKey: "bankingSystemConfigs" },
            { title: "Exports", url: "https://docs.devcore.cz/resources/banking-system/configs", contentKey: "bankingSystemExports" }
          ]
        },
        {
          name: "Realistic Needs",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/realistic-needs", contentKey: "realisticNeedsGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/realistic-needs/installation", contentKey: "realisticNeedsInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/realistic-needs/configs", contentKey: "realisticNeedsConfigs" },
            { title: "Exports", url: "https://docs.devcore.cz/resources/realistic-needs/exports/getalcoholvalue", contentKey: "realisticNeedsExports" }
          ]
        },
        {
          name: "Mileage Meter",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/mileage-meter", contentKey: "mileageMeterGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/mileage-meter/installation", contentKey: "mileageMeterInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/mileage-meter/config", contentKey: "mileageMeterConfigs" }
          ]
          // subsections: [
          //   { title: "Installation", url: "https://docs.devcore.cz/resources/mileage-meter/installation", contentKey: "mileageMeterInstallation" },
          //   { title: "Config", url: "https://docs.devcore.cz/resources/mileage-meter/config", contentKey: "mileageMeterConfig" }
          // ]
        },
        {
          name: "Water Gun",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/mileage-meter", contentKey: "waterGunGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/water-gun/installation", contentKey: "waterGunInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/water-gun/config", contentKey: "waterGunConfigs" },
            { title: "Exports", url: "https://docs.devcore.cz/resources/water-gun/exports", contentKey: "waterGunExports" }
          ]
        },
        {
          name: "Multijob",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/multijob", contentKey: "multijobGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/multijob/installation", contentKey: "multijobInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/multijob/config", contentKey: "multijobConfigs" },
          ]
        },
        {
          name: "Key Interactions",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/key-interactions", contentKey: "keyInteractionsGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/key-interactions/installation", contentKey: "keyInteractionsInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/key-interactions/configs", contentKey: "keyInteractionsConfigs" },
            { title: "Exports", url: "https://docs.devcore.cz/resources/key-interactions/exports", contentKey: "keyInteractionsExports" }
          ]
        },
        {
          name: "NPC Drug Sales",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/npc-drug-sales", contentKey: "npcDrugSalesGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/npc-drug-sales/installation", contentKey: "npcDrugSalesInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/npc-drug-sales/configs", contentKey: "npcDrugSalesConfigs" }
          ]
        },
        {
          name: "Tag System",
          subsections: [
            { title: "Getting Started", url: "https://docs.devcore.cz/resources/tag-system", contentKey: "tagSystemGettingStarted" },
            { title: "Installation", url: "https://docs.devcore.cz/resources/tag-system/installation", contentKey: "tagSystemInstallation" },
            { title: "Configs", url: "https://docs.devcore.cz/resources/tag-system/config", contentKey: "tagSystemConfigs" },
            { title: "Exports", url: "https://docs.devcore.cz/resources/tag-system/exports", contentKey: "tagSystemExports" }
          ]
        },
      ]
    }
  ];

  // Detailed content for each documentation section, pulled from research notes
  const contentMap = {
    gettingStarted: `
      <h3 class="text-2xl font-bold mb-6">Getting Started with DEVCORE Scripts</h3>

      <div class="mb-8">
        <h4 class="text-xl font-semibold mb-4 text-devcore.primary">Purschasing & Installation</h4>
        <div class="bg-devcore.dark p-4 rounded-lg mb-4">
          <div class="font-mono text-sm text-gray-300">
            <p>1. Buy the script on our Tebex store</p>
            <p>2. Download the script package from your keymaster</p>
            <p>3. Extract the script to your server's resources folder</p>
            <p>4. Add "ensure devcore_scriptname" to your server.cfg</p>
            <p>5. Restart your server</p>
          </div>
        </div>
        <p class="text-gray-300">Make sure you have the latest version of FiveM and any required dependencies installed.</p>
      </div>

      <div class="mb-8">
        <h4 class="text-xl font-semibold mb-4 text-devcore.primary">Configuration</h4>
        <p class="text-gray-300 mb-4">Most DEVCORE scripts come with a comprehensive configuration file that allows you to customize the script to your server's needs.</p>

        <p class="text-gray-300">After making changes to the configuration, restart the resource for changes to take effect.</p>
      </div>

      <div>
        <h4 class="text-xl font-semibold mb-4 text-devcore.primary">First Steps</h4>
        <p class="text-gray-300 mb-4">Once installed and configured, you can begin using the script's features. Here are some common first steps:</p>

        <div class="space-y-3">
          <div class="flex items-start">
            <div class="flex-shrink-0 mt-1">
              <div class="w-6 h-6 rounded-full bg-devcore.primary bg-opacity-20 flex items-center justify-center text-devcore.primary">
                <i class="fas fa-check text-xs"></i>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-gray-300">Test basic functionality in-game to ensure everything is working as expected.</p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="flex-shrink-0 mt-1">
              <div class="w-6 h-6 rounded-full bg-devcore.primary bg-opacity-20 flex items-center justify-center text-devcore.primary">
                <i class="fas fa-check text-xs"></i>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-gray-300">Check server logs for any errors or warnings that might need attention.</p>
            </div>
          </div>

          <div class="flex items-start">
            <div class="flex-shrink-0 mt-1">
              <div class="w-6 h-6 rounded-full bg-devcore.primary bg-opacity-20 flex items-center justify-center text-devcore.primary">
                <i class="fas fa-check text-xs"></i>
              </div>
            </div>
            <div class="ml-3">
              <p class="text-gray-300">Consider creating backups before making extensive customizations.</p>
            </div>
          </div>
        </div>
      </div>
    `,
    // Realistic Smoking
    realisticSmokingGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Realistic Smoking - V3.3</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Compatible with ESX / QB / QBX, the script itself detects which framework you are using</li>
        <li class="mb-1"><b>Compatible for ox_inventory / qs_inventory / qb_inventory. (Script works for other inventory systems but without saving metadata)</li>
        <li class="mb-1"><b>Optimized 0.01ms</li>
        <li class="mb-1"><b>Brand new code compared to version 2.0</li>
        <li class="mb-1"><b>Very extensive configuration both for the script itself and for individual items</li>
        <li class="mb-1"><b>Synchronized smoke effects</li>
        <li class="mb-1"><b>It is possible to smoke, Bongs / Joints / Vapes / Puff Vapes / Cigarettes / Cigars</li>
        <li class="mb-1"><b>Custom animations (Using bong, Rolling joints, Lighting cigarettes/joints/cigar)</li>
        <li class="mb-1"><b>For each item it is possible to adjust the Exhale Effect and smoke particles</li>
        <li class="mb-1"><b>Rollings joints with a minigame, it is possible to disable the mini-game in the configuration</li>
        <li class="mb-1"><b>After using a cigarette, cigar or joint you need to use the lighter in the inventory or press the Light button to light it</li>
        <li class="mb-1"><b>Before lighting a cigarette, cigar or joint, the unlit prop is set and after lighting the prop is changed</li>
        <li class="mb-1"><b>It is possible to add more liquids, cigarette packs, rollings papers, lighters or create new items like (cigars, cigarettes, joints, bongs, puff vapes, vapes)</li>
        <li class="mb-1"><b>Compatible with xsound, it is possible to add sound to any animation</li>
        <li class="mb-1"><b>A dropped item ignites spilled gasoline on the ground</li>
        <li class="mb-1"><b>Before lighting a Cigarette, Cigar or Joint, the items can be placed in a position behind the ear</li>
        <li class="mb-1"><b>You can set the high effect to any item just like other effects</li>
        <li class="mb-1"><b>Each exhale of smoke from a joint, bong or other item that has a set High value will increase the high effect</li>
        <li class="mb-1"><b>High effect drops automatically every 15 seconds time is adjustable</li>
        <li class="mb-1"><b>Compatible with our Key Interactions script</li>
        <li class="mb-1"><b>Possibility to disable the Options menu</li>
        <li class="mb-1"><b>Possibility to give players items using ox_target / qb-target</li>
        <li class="mb-1"><b>Option to reduce Hunger and Thirst when smoking (by default only set to joints and bong)</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    realisticSmokingInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Realistic Smoking Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_smoking in server.cfg after your framework and ox_lib</p>
          <p class="mb-1">4. Now create items depending on the inventory</p>
          
          <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
            <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
            <div>
              <p class="text-gray-300 mb-1">If you are using <b>ox_inventory</b>: paste the contents of the file <code>upload/inventory/ox_inventory.txt</code> into the file <code>ox_inventory/data/items.lua</code>. And upload <code>upload/icons</code> into <code>ox_inventory/web/images</code>.</p>
              <p class="text-gray-300 mb-1">If you are using <b>qs-inventory</b>: paste the contents of the file <code>upload/inventory/qs_inventory.txt</code> into the file <code>qs-inventory/shared/items.lua</code>. And upload <code>uplod/icons</code> into <code>qs-inventory/html/images</code>.</p>
              <p class="text-gray-300 mb-1">If you are using <b>qb-inventory</b>: paste the contents of the file <code>upload/inventory/qb_inventory.txt</code> into the file <code>qb-core/shared/items.lua</code>. And upload <code>upload/icons</code> into <code>qb-inventory/html/images</code>.</p>
              <p class="text-gray-300">If you are using a <b>different inventory</b>: paste the contents of the file <code>upload/inventory/items.sql</code> file into the database.</p>
            </div>
          </div>

          <p class="mb-1">5. If you want to use xsound for sound effects, you can find it here: <a href="https://github.com/Xogy/xsound/releases/tag/1.4.3" target="_blank" rel="noopener noreferrer">https://github.com/Xogy/xsound/releases/tag/1.4.3</a></p>
          <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
            <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
            <div>
              <p class="text-gray-300 mb-1">If you have an xsound script running on your server, put the files from <code>devcore_smoke/upload/xsound</code> into <code>xsound/html/sounds</code</p>
            </div>
          </div>
          <p class="mb-1">6. For devcore_smoke to work properly, ox_lib needs to be added to the server: <a href="https://github.com/overextended/ox_lib/releases" target="_blank" rel="noopener noreferrer">https://github.com/overextended/ox_lib/releases</a></p>
          <p class="mb-1">6. Edit the config as you wish and enjoy your new script. 😄</p>
        </div>
      </div>
    `,
    realisticSmokingExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Realistic Smoking Exports</h4>
      <p class="text-gray-300 mb-4">The Devcore Smoking provides robust client-side export for seamless integration with other scripts and custom functionalities on your FiveM server. this export allow for programmatic control over key smoking features, enhancing the flexibility and extensibility of your server.</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">1. Remove high effect</h5>
        <p class="text-gray-300 mb-2">This client-side export is used to programmatically remove the High effect of a player.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_smoke']:RemoveHighEffect()
            </code>
          </pre>
        </div>
        </div>
      </div>
    `,
    // Banking System
    bankingSystemGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Banking System - V1.0</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Compatible with ESX</li>
        <li class="mb-1"><b>Fully configurable banking system, UI, notifications and translations</li>
        <li class="mb-1"><b>Ability to transfer money to offline players</li>
        <li class="mb-1"><b>Ability to select from near players in transfer for easier money transfer</li>
        <li class="mb-1"><b>Integrated logs</li>
        <li class="mb-1"><b>Modern responsive UI</li>
        <li class="mb-1"><b>Optimized performance with minimal resource impact</li>
        <li class="mb-1"><b>Multiple notification system support (ox_lib, ESX, okok, cd, mythic, pnotify)</li>
        <li class="mb-1"><b>Multiple open banking system support (ox_lib textui, ESX textui, okok textui, cd textui, ox target)</li>
        <li class="mb-1"><b>Configurable loan requirements</li>
        <li class="mb-1"><b>Localization support</li>
        <li class="mb-1"><b>Configurable objects and zones for ox target</li>
        <li class="mb-1"><b>Card limit system</li>
        <li class="mb-1"><b>Society account management</li>
        <li class="mb-1"><b>Debug mode and export functions for integration</li>
        <li class="mb-1"><b>Automatic bank accounts and cards creation</li>
        <li class="mb-1"><b>Persistence storage with reliable database handling</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    bankingSystemInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Banking System Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_banking in server.cfg after your framework and database</p>
          <p class="mb-1">4. Insert your database query from insert.sql into your database</p>

          <p class="mb-1">5. Put this at the end of es_extended/server/main.lua:</p>
          <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
          <div class="bg-devcore.dark p-4 rounded-lg mb-1">
            <div class="flex space-x-2 mb-3">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
              <code>
    RegisterServerEvent("devcore_banking:save", function(money, bankMoney, message)
        if money or bankMoney then
            DropPlayer(source, message)
        else
            Core.SavePlayers()
        end
    end)
              </code>
            </pre>
          </div>
          </div>
          <p class="mb-1">6. Edit the config as you wish and enjoy your new script. 😄</p>
        <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
        <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
        <div>
          <p class="text-gray-300">If you want to change locales of the UI, we have a tutorial in our docs!</p>
        </div>
      </div>
      <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
        <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
        <div>
          <p class="text-gray-300">You can edit locales in <code>web/src/locales</code>.</p>
        </div>
      </div>
      <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-4 flex items-center gap-3">
        <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
        <div>
          <p class="text-gray-300">We don't recommend making any changes to the UI/style of the UI, as it can easily break the interface.</p>
        </div>
      </div>
        </div>
      </div>
    `,
    bankingSystemExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Banking System Exports (Server-Side)</h4>
      <p class="text-gray-300 mb-4">The Devcore Banking System provides robust server-side exports for seamless integration with other scripts and custom functionalities on your FiveM server. These exports allow for programmatic control over key banking features, enhancing the flexibility and extensibility of your server's economy.</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">1. Generate Bank Card</h5>
        <p class="text-gray-300 mb-2">This server-side export is used to programmatically generate a new bank card for a player.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
local success, result = exports['devcore_banking']:generateBankCard(playerId)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>playerId</code>: The ID of the player.</li>
        </ul>
        <p class="text-gray-300 mb-1"><b>Callback (<code>success</code>)</b>: <code>true</code> if the card was successfully created, <code>false</code> otherwise.</p>
        <p class="text-gray-300"><b>Callback (<code>result</code>)</b>: Only returned if <code>success</code> is <code>false</code>, providing the reason why the card wasn't created.</p>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">2. Check Card Limit</h5>
        <p class="text-gray-300 mb-2">This server-side export allows you to check if a specific transaction amount falls within a player's defined card limit.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
local inLimit = exports['devcore_banking']:checkLimit(playerId, amount, limitType)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>playerId</code>: The ID of the player.</li>
          <li><code>amount</code>: The transaction amount to check.</li>
          <li><code>limitType</code>: The type of limit to check (<code>'general'</code> or <code>'atm'</code>).</li>
        </ul>
        <p class="text-gray-300"><b>Callback</b>: <code>true</code> if the amount fits within the limit, <code>false</code> otherwise.</p>
      </div>
    `,
    // Realistic Needs
    realisticNeedsGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Realistic Needs - V3.7</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Compatible with ESX / QB / QBX, the script itself detects which framework you are using.</li>
        <li class="mb-1"><b>0 ms when inactive</li>
        <li class="mb-1"><b>Compatible for ox_inventory / qb_inventory</li>
        <li class="mb-1"><b>Customizable menu</li>
        <li class="mb-1"><b>Options menu for better clarity</li>
        <li class="mb-1"><b>Floating menu for items on the ground</li>
        <li class="mb-1"><b>Three phases of movement according to alcohol level</li>
        <li class="mb-1"><b>Movement interaction in drink driving</li>
        <li class="mb-1"><b>Breathalyzer</li>
        <li class="mb-1"><b>Pouring drinks into glasses</li>
        <li class="mb-1"><b>Automatic consumption</li>
        <li class="mb-1"><b>Placing all items on the ground (it is possible not to store items placed on the ground in the database)</li>
        <li class="mb-1"><b>Serving items to players around you</li>
        <li class="mb-1"><b>Posibility to add a second prop for example to hold a fork</li>
        <li class="mb-1"><b>Custom animation when pouring drinks</li>
        <li class="mb-1"><b>Auto take (for ox_inventory if you have an item in your inventory that is set to auto takeover, then this item is automatically used)</li>
        <li class="mb-1"><b>Compatible with our key interaction system</li>
        <li class="mb-1"><b>Possibility to disable the options menu</li>
        <li class="mb-1"><b>Possibility to give players items using ox_target / qb-target</li>
        <li class="mb-1"><b>Animation when testing with alcohol tester yourself</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    realisticNeedsInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Realistic Needs Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_needs in server.cfg after your framework and database</p>
          <p class="mb-1">4. Now create items depending on the inventory</p>

          <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
            <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
            <div>
              <p class="text-gray-300 mb-1">If you are using <b>ox_inventory</b>: paste the contents of the file <code>upload/inventory/ox_inventory.txt</code> into the file <code>ox_inventory/data/items.lua</code>. And upload <code>upload/icons</code> into <code>ox_inventory/web/images</code>.</p>
              <p class="text-gray-300 mb-1">If you are using <b>qs-inventory</b>: paste the contents of the file <code>upload/inventory/qs_inventory.txt</code> into the file <code>qs-inventory/shared/items.lua</code>. And upload <code>uplod/icons</code> into <code>qs-inventory/html/images</code>.</p>
              <p class="text-gray-300 mb-1">If you are using <b>qb-inventory</b>: paste the contents of the file <code>upload/inventory/qb_inventory.txt</code> into the file <code>qb-core/shared/items.lua</code>. And upload <code>upload/icons</code> into <code>qb-inventory/html/images</code>.</p>
              <p class="text-gray-300">If you are using a <b>different inventory</b>: paste the contents of the file <code>upload/inventory/items.sql</code> file into the database.</p>
            </div>
          </div>
          <p class="mb-1">5. If you want to store the positions of items placed on the ground in the database, don't forget to upload the upload/sql/needs.sql file to the database</p>

          <p class="mb-1">6. Go to esx_basicneeds/config.lua and delete all created items</p>
          <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
          <div class="bg-devcore.dark p-4 rounded-lg mb-1">
            <div class="flex space-x-2 mb-3">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
              <code>
-- Default --
Config.Items = {
	["bread"] = {
		type = "food",
		prop= "prop_cs_burger_01",
		status = 200000,
		remove = true
	},
	
	["water"] = {
		type = "drink",
		prop = "prop_ld_flow_bottle",
		status = 100000,
		remove = true
	}
}

-- After removed --
Config.Items = {

}
              </code>
            </pre>
          </div>
          </div>
          <p class="mb-1">6. Edit the config as you wish and enjoy your new script. 😄</p>
        </div>
      </div>
    `,
    realisticNeedsExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Realistic Needs Exports (Client-Side)</h4>
      <p class="text-gray-300 mb-4">The Realistic Needs script now includes only one export for integration with other scripts. Export which allows other scripts to retrieve the player's current alcohol level.</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">1. Get Alcohol Value</h5>
        <p class="text-gray-300 mb-2">This client-side export is used to get the alcohol value of a player which trigger the export.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
local alcohol_value = exports['devcore_needs']:GetAlcoholValue()
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Callback (<code>alcohol_value</code>)</b>: returns the player's current alcohol value</p>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">2. Is Player Consume</h5>
        <p class="text-gray-300 mb-2">This server-side export is used to get status of consuming of the player.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
local consume = exports['devcore_needs']:IsPlayerConsume(playerId)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>playerId</code>: The ID of the player.</li>
        </ul>
        <p class="text-gray-300 mb-1"><b>Callback (<code>consume</code>)</b>: returns the player's current consume status</p>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">3. Remove Alcohol Effect</h5>
        <p class="text-gray-300 mb-2">This client-side export is used to remove the alcohol effect from the player.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_needs']:RemoveAlcoholEffect()
            </code>
          </pre>
        </div>
        </div>
      </div>
    `,
    // Mileage Meter
    mileageMeterGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Mileage Meter - V2.0</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Version 2.0 has completely new code from the previous version</li>
        <li class="mb-1"><b>Mileage only counts for vehicles that someone owns</li>
        <li class="mb-1"><b>The more kilometers the vehicle has, the lower the power and the dirtier the vehicle, this function can be switched off</li>
        <li class="mb-1"><b>If the vehicle has twist mileage, the dirtiness of the vehicle and the wear of the engine depend on the actual mileage which can only be found by decrypting the twist mileage</li>
        <li class="mb-1"><b>The appearance of the 2d menu can be customized in the configuration</li>
        <li class="mb-1"><b>Mileage can also be displayed using notifications</li>
        <li class="mb-1"><b>0 ms when inactive</li>
        <li class="mb-1"><b>Extensive Configuration</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    mileageMeterInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Mileage Meter Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_mileagemeter in server.cfg after your framework and database</p>
          <p class="mb-1">4. Upload the mileagemeter.sql file to the database</p>
          <p class="mb-1">5. Now create items depending on the inventory</p>
          <p class="mb-1">Here are the items to put into <code>ox_inventory/data/items.lua</code>:</p>
          <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
          <div class="bg-devcore.dark p-4 rounded-lg mb-1">

            <div class="flex space-x-2 mb-3">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
              <code>
	['twist_mil'] = {
		label = 'USB-twist mileage',
		stack = true,
		close = true,
		description = "For to reduce vehicle mileage"
	},

	['mil_decrypter'] = {
		label = 'USB-decryped mileage',
		stack = true,
		close = true,
		description = "For to decode the reduction in mileage"
	},
  </code>
            </pre>
          </div>
          </div>

          <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
            <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
            <div>
              <p class="text-gray-300 mb-1">If you use another inventory, use its method to add items</p>
            </div>
          </div>
          
          <p class="mb-1">6. Edit the config as you wish and enjoy your new script. 😄</p>
        </div>
      </div>
    `,
    // Water Gun
    waterGunGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Water Gun - V2.6</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Compatible with ESX / QB / QBX / STANDALONE (export), the script itself detects which framework you are using.</li>
        <li class="mb-1"><b>Brand new code compared to v1</li>
        <li class="mb-1"><b>Need to fill the water gun tank with water, the maximum amount of water in the tank can be changed in the configuration</li>
        <li class="mb-1"><b>The water gun also needs to be pumped up to give it a longer hit range</li>
        <li class="mb-1"><b>The player has wet clothes after the hit</li>
        <li class="mb-1"><b>Synchronized water effects</li>
        <li class="mb-1"><b>Custom water gun prop</li>
        <li class="mb-1"><b>Script is optimized</li>
        <li class="mb-1"><b>Saving metadata in items</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    waterGunInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Water Gun Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_watergun in server.cfg after your framework</p>
          <p class="mb-1">4. Now create items depending on the inventory</p>
          
<p class="mb-1">Here are the items to put into <code>ox_inventory/data/items.lua</code>:</p>
<div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
          <div class="bg-devcore.dark p-4 rounded-lg mb-1">

            <div class="flex space-x-2 mb-3">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
              <code>
	['watergun'] = {
		label = 'Water Gun',
    weight = 500,
		stack = false,
		close = true,
		client = {
			export = 'devcore_watergun.useWaterGun'
		}
	},
  </code>
            </pre>
          </div>
          </div>

          <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
            <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
            <div>
              <p class="text-gray-300 mb-1">If you are using qs-inventory or qb-inventory
paste this into the file qb-core/shared/items.lua</p>
<div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
          <div class="bg-devcore.dark p-4 rounded-lg mb-1">

            <div class="flex space-x-2 mb-3">
              <div class="w-3 h-3 rounded-full bg-red-500"></div>
              <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
              <div class="w-3 h-3 rounded-full bg-green-500"></div>
            </div>
            <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
              <code>
	['twist_mil'] = {
		label = 'USB-twist mileage',
		stack = true,
		close = true,
		description = "For to reduce vehicle mileage"
	},

	['mil_decrypter'] = {
		label = 'USB-decryped mileage',
		stack = true,
		close = true,
		description = "For to decode the reduction in mileage"
	},
  </code>
            </pre>
          </div>
          </div>
              <p class="text-gray-300 mb-1">If you use another inventory, you use its method to add the item</p>
            </div>
          </div>
          
          <p class="mb-1">6. Edit the config as you wish and enjoy your new script. 😄</p>
        </div>
      </div>
    `,
    waterGunExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Water Gun Exports (Client-Side)</h4>
      <p class="text-gray-300 mb-4">The Water Gun script now includes only one export for integration with other scripts. Export to start watergun</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Use Water Gun</h5>
        <p class="text-gray-300 mb-2">This client-side export is used to start water gun.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_watergun']:useWaterGun(item)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>item</code>: Item name from Config.WaterGuns.</li>
        </ul>
      </div>
    `,
    // Multijob
    multijobGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Multijob - V3.0</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Compatible with ESX / QB / QBX</li>
        <li class="mb-1"><b>Compatible with our banking system</li>
        <li class="mb-1"><b>Fully configurable system for jobs, UI, notifications and translations</li>
        <li class="mb-1"><b>Support for up to 3 simultaneous jobs with automatic rotation</li>
        <li class="mb-1"><b>Admin commands for job management and player monitoring</li>
        <li class="mb-1"><b>Modern responsive UI with smooth animations</li>
        <li class="mb-1"><b>Optimized performance with minimal resource impact</li>
        <li class="mb-1"><b>Multiple notification system support (ox_lib, ESX, custom)</li>
        <li class="mb-1"><b>Configurable sound effects and keybinds</li>
        <li class="mb-1"><b>Complete localization support</li>
        <li class="mb-1"><b>Maintains job grades when switching</li>
        <li class="mb-1"><b>MySQL async operations for better performance</li>
        <li class="mb-1"><b>Debug mode and export functions for integration</li>
        <li class="mb-1"><b>Fully customizable UI appearance</li>
        <li class="mb-1"><b>Automatic job saving and conflict prevention</li>
        <li class="mb-1"><b>Persistence storage with reliable database handling</li>
        <li class="mb-1"><b>Bossmenu for factions</li>
        <li class="mb-1"><b>Possibility to view employee hours</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    multijobInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Multijob Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_multijob in server.cfg after your framework and database</p>
          <p class="mb-1">4. Insert your database query from insert.sql into your database</p>
          <p class="mb-1">5. Edit the config as you wish and enjoy your new script. 😄</p>
          <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg mb-2 flex items-center gap-3">
            <i class="fas fa-info-circle text-blue-400 text-xl flex-shrink-0"></i>
            <div>
              <p class="text-gray-300 mb-1">The script takes the names of jobs and job positions from your job database</p>
            </div>
          </div>
        </div>
      </div>
    `,
    // NPC Drug Sales
    npcDrugSalesGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">NPC Drug Sales - Overview</h4>
      <p class="text-gray-300 mb-4">Advanced system for selling custom drugs to NPCs with dynamic chances, dispatch hooks, and localization.</p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Frameworks:</b> ESX, QBCore, or QBX.</li>
        <li class="mb-1"><b>Targeting:</b> <code>ox_target</code> or <code>qb-target</code>.</li>
        <li class="mb-1"><b>Notifications:</b> <code>ox_lib</code> or your own replacement.</li>
        <li class="mb-1"><b>Dispatch (optional):</b> <code>cd_dispatch</code>, <code>qs-dispatch</code>, or <code>core_dispatch</code>.</li>
        <li class="mb-1"><b>Support:</b> <a href="https://discord.gg/zcG9KQj3sa" target="_blank" rel="noopener noreferrer">Discord</a>, <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">Store</a>, <a href="https://docs.devcore.cz/" target="_blank" rel="noopener noreferrer">Docs</a>.</li>
      </ul>
      <p class="text-gray-300">Use this script to add immersive street dealing with configurable risk/reward.</p>
    `,
    npcDrugSalesInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">NPC Drug Sales Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. Download or clone <code>devcore_npcdrugs</code> into your <code>resources/</code> folder.</p>
          <p class="mb-1">2. Add <code>ensure devcore_npcdrugs</code> to <code>server.cfg</code> after your framework and inventory system.</p>
          <p class="mb-1">3. Configure <code>config.lua</code> (drugs, chances, dispatch, language, conditions).</p>
          <p class="mb-1">4. Restart the server (or restart the resource).</p>
          <p class="mb-1">5. Test a sell interaction in-game.</p>
        </div>
      </div>
      <p class="text-gray-300">Tip: keep dispatch optional if you want quieter street sales; enable it for higher risk roleplay.</p>
    `,
    npcDrugSalesConfigs: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">NPC Drug Sales Configuration</h4>
      <p class="text-gray-300 mb-4">All settings live in <code>config.lua</code>. Key areas to tune:</p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li><b>Drug items & pricing:</b> Define items, sell price ranges, and bonuses.</li>
        <li><b>Chances & behavior:</b> Success/fail odds, NPC reaction (accept/refuse/call police), interaction range, allowed hours, bonus multipliers.</li>
        <li><b>Dispatch:</b> Pick a dispatch system and set alert odds and messages.</li>
        <li><b>Conditions:</b> Minimum police online or job-based requirements to start selling.</li>
        <li><b>Localization:</b> Built-in English and Czech; extend locales directly in <code>config.lua</code>.</li>
        <li><b>Notifications/targets:</b> Choose notification handler and target resource (<code>ox_target</code>/<code>qb-target</code>).</li>
        <li><b>Debug:</b> Set <code>Config.Debug = true</code> for spawn, chance, and interaction logs during testing.</li>
      </ul>
      <p class="text-gray-300">Iterate with debug on, then disable it for production.</p>
    `,
    // Key Interactions (KeypressUI)
    keyInteractionsGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Key Interactions (KeypressUI)</h4>
      <p class="text-gray-300 mb-4">KeypressUI is an advanced system for displaying contextual key/action UI in FiveM with flexible timing, positioning, styling, and sound controls.</p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Interactive prompts:</b> Show temporary, timed, or permanent overlays for actions, menus, and notifications.</li>
        <li class="mb-1"><b>Modern data format:</b> Supports both legacy associative tables and the recommended array-of-objects format for multiple keys.</li>
        <li class="mb-1"><b>Precise placement:</b> Preset positions (left, center, right) plus pixel offsets for fine tuning.</li>
        <li class="mb-1"><b>Rich styling:</b> Hex/background colors, per-key colors, glow/shadow controls, and ready-made palettes.</li>
        <li class="mb-1"><b>Sound control:</b> Global toggle and per-UI overrides for feedback.</li>
        <li class="mb-1"><b>Full API:</b> Exports and events to show, update, hide, or enumerate active UIs without recreating them.</li>
      </ul>
      <p class="text-gray-300">Use it to surface actions in any Devcore script (or your own) with minimal code.</p>
    `,
    keyInteractionsInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Key Interactions Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, download it from <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a>.</p>
          <p class="mb-1">2. Place <code>devcore_keyinteraction</code> in your server resources.</p>
          <p class="mb-1">3. Add <code>ensure devcore_keyinteraction</code> to <code>server.cfg</code> after your framework and <code>ox_lib</code> (if used).</p>
          <p class="mb-1">4. Restart the server or just restart the resource.</p>
        </div>
      </div>
      <p class="text-gray-300">Optional: call <code>SetKeypressUISettings</code> on resource start to set defaults like position, offsets, and sound.</p>
    `,
    keyInteractionsConfigs: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">KeypressUI Usage & Configs</h4>
      <p class="text-gray-300 mb-4">Configure how prompts look, where they appear, and how long they stay visible.</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Data Formats</h5>
        <p class="text-gray-300 mb-2">Both legacy associative tables and the recommended array-of-objects format are supported.</p>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>-- Legacy (associative)
local data = {
    E = "Interact",
    F = "Use Item",
    G = "Open Menu"
}

-- Recommended (array of objects)
local data = {
    {key = "E", action = "Interact"},
    {key = "F", action = "Use Item"},
    {key = "G", action = "Open Menu"},
    {key = "SPACE", action = "Jump"},
    {key = "CTRL", action = "Crouch"}
}</code></pre>
        </div>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Duration Options</h5>
        <ul class="list-disc list-inside text-gray-300 mb-3 space-y-1">
          <li><code>-1</code> Permanent display.</li>
          <li><code>0</code> Manual hide only (no timer).</li>
          <li><code>&gt; 0</code> Milliseconds until auto-hide.</li>
          <li><code>nil</code> Default duration (5 minutes).</li>
        </ul>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>exports['devcore_keyinteraction']:ShowKeypressUI('temp', data, 5000)     -- 5s
exports['devcore_keyinteraction']:ShowKeypressUI('permanent', data, -1) -- Permanent
exports['devcore_keyinteraction']:ShowKeypressUI('default', data)       -- 5 minutes</code></pre>
        </div>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Positioning & Offsets</h5>
        <p class="text-gray-300 mb-3">Use presets or pixel offsets for fine control.</p>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>-- Bottom right (default)
exports['devcore_keyinteraction']:ShowPermanentKeypressUI('right_ui', data, { position = 'right' })

-- Bottom left
exports['devcore_keyinteraction']:ShowPermanentKeypressUI('left_ui', data, { position = 'left' })

-- Center with offsets
exports['devcore_keyinteraction']:ShowPermanentKeypressUI('center_ui', data, {
    position = 'center', offsetX = 0, offsetY = -50
})

-- Custom offsets on right side
exports['devcore_keyinteraction']:ShowPermanentKeypressUI('custom_pos', data, {
    position = 'right', offsetX = -100, offsetY = -100
})</code></pre>
        </div>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Advanced Colors</h5>
        <p class="text-gray-300 mb-3">Apply a single hex color or a full colorOptions object.</p>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>-- Simple hex
exports['devcore_keyinteraction']:ShowPermanentKeypressUI('simple_color', data, {
    hexColor = '#ff6b6b'
})

-- Full color options
local myColors = {
    hexColor = '#2c3e50',
    keyColor = '#e74c3c',
    shadowColor = '#c0392b',
    shadowIntensity = 1.0,
    shadowBlur = 8
}

exports['devcore_keyinteraction']:ShowPermanentKeypressUI('colored_ui', data, {
    colorOptions = myColors
})</code></pre>
        </div>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Sound Control</h5>
        <p class="text-gray-300 mb-3">Toggle globally or per UI.</p>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>exports['devcore_keyinteraction']:SetKeypressUISound(true)   -- Enable globally
exports['devcore_keyinteraction']:SetKeypressUISound(false)  -- Disable globally

exports['devcore_keyinteraction']:ShowKeypressUI('silent', data, -1, {
    playSound = false -- Disable sound for this UI only
})</code></pre>
        </div>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Ready-to-Use Palettes</h5>
        <p class="text-gray-300 mb-3">Drop-in color combinations for common states.</p>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>local palettes = {
    success = { hexColor = '#27ae60', keyColor = '#ffffff', shadowColor = '#2ecc71' },
    danger  = { hexColor = '#e74c3c', keyColor = '#ffffff', shadowColor = '#c0392b', shadowIntensity = 1.0 },
    info    = { hexColor = '#3498db', keyColor = '#ffffff', shadowColor = '#2980b9' },
    warning = { hexColor = '#f39c12', keyColor = '#ffffff', shadowColor = '#e67e22' },
    neutral = { hexColor = '#34495e', keyColor = '#ecf0f1', shadowColor = '#2c3e50' }
}</code></pre>
        </div>
      </div>

      <div class="mb-2">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Quick Example</h5>
        <p class="text-gray-300 mb-2">A simple vehicle interaction prompt with color styling.</p>
        <div class="bg-devcore.dark p-4 rounded-lg">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>exports['devcore_keyinteraction']:ShowPermanentKeypressUI('vehicle_interact', {
    {key = 'F', action = 'Enter Vehicle'},
    {key = 'H', action = 'Open Hood'},
    {key = 'L', action = 'Lock/Unlock'},
    {key = 'K', action = 'Engine On/Off'}
}, {
    position = 'center',
    hexColor = '#3498db',
    keyColor = '#ffffff',
    shadowColor = '#2980b9'
})</code></pre>
        </div>
      </div>
    `,
    keyInteractionsExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">KeypressUI Exports & Events</h4>
      <p class="text-gray-300 mb-4">Control every UI element through exports or matching events.</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Display & Update</h5>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>exports['devcore_keyinteraction']:ShowKeypressUI(id, data, duration, options)
exports['devcore_keyinteraction']:ShowPermanentKeypressUI(id, data, options) -- duration = -1 shortcut
exports['devcore_keyinteraction']:UpdateKeypressUI(id, data, colorOptions)
exports['devcore_keyinteraction']:HideKeypressUI(id)
exports['devcore_keyinteraction']:HideAllKeypressUI()</code></pre>
        </div>
        <ul class="list-disc list-inside text-gray-300 space-y-1">
          <li><code>id</code>: Unique UI identifier.</li>
          <li><code>data</code>: Key/action table (legacy or new format).</li>
          <li><code>duration</code>: <code>-1</code> permanent, <code>0</code> manual hide, or milliseconds.</li>
          <li><code>options</code>: Positioning (<code>position</code>, <code>offsetX</code>, <code>offsetY</code>), <code>hexColor</code> or <code>colorOptions</code>, <code>playSound</code>, etc.</li>
          <li><code>colorOptions</code>: Optional override when updating without changing duration/position.</li>
        </ul>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Utility</h5>
        <div class="bg-devcore.dark p-4 rounded-lg mb-3">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>local isPermanent = exports['devcore_keyinteraction']:IsKeypressUIPermanent('ui_id')
local activeUIs = exports['devcore_keyinteraction']:GetActiveKeypressUIs()
exports['devcore_keyinteraction']:SetKeypressUISettings({
    position = 'center', offsetX = 0, offsetY = -100, soundEnabled = true
})
exports['devcore_keyinteraction']:SetKeypressUISound(false) -- Global toggle</code></pre>
        </div>
        <p class="text-gray-300">Use these helpers to introspect UIs or set defaults before showing anything.</p>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Events</h5>
        <div class="bg-devcore.dark p-4 rounded-lg">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto"><code>TriggerEvent('devcore_keyinteraction:show', 'event_ui', data, 5000, options)
TriggerEvent('devcore_keyinteraction:showPermanent', 'permanent_ui', data, options)
TriggerEvent('devcore_keyinteraction:hide', 'ui_id')
TriggerEvent('devcore_keyinteraction:hideAll')
TriggerEvent('devcore_keyinteraction:update', 'ui_id', newData, newColors)</code></pre>
        </div>
        <p class="text-gray-300 mt-2">Events mirror the exports, allowing you to trigger prompts from other resources without direct exports.</p>
      </div>

      <div class="bg-blue-900/20 border border-blue-700/50 p-4 rounded-lg flex items-center gap-3">
        <i class="fas fa-lightbulb text-blue-400 text-xl flex-shrink-0"></i>
        <p class="text-gray-300 mb-0">Tips: hide permanent UIs when not needed, avoid over-updating (max ~10-20 times/sec), and keep colors consistent for UX.</p>
      </div>
    `,
    // TAG SYSTEM
    tagSystemGettingStarted: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Tag System - V1.0</h4>
      <p class="text-gray-300 mb-4">The script uses the escrow system</a></p>
      <ul class="list-disc list-inside text-gray-300 mb-4 space-y-1">
        <li class="mb-1"><b>Compatible with ESX / QB / QBX and also STANDALONE</li>
        <li class="mb-1"><b>Sync with other players</li>
        <li class="mb-1"><b>For ESX using group system (xPlayer.getGroup()) or with exports</li>
        <li class="mb-1"><b>For QBCore/QBX/Standalone using ACE permissions in server.cfg or with exports</li>
        <li class="mb-1"><b>Displaying ui in 3d space</li>
        <li class="mb-1"><b>Optimized performance</li>
      </ul>
      <h5 class="text-xl mb-4">Server artifacts</h5>
      <p class="text-gray-300 mb-4">Make sure your server artifacts version is above the 5181</p>
    `,
    tagSystemInstallation: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Tag System Installation</h4>
      <div class="bg-devcore.dark p-4 rounded-lg mb-4">
        <div class="font-mono text-sm text-gray-300">
          <p class="mb-1">1. After purchasing the script on the store <a href="https://store.devcore.cz/" target="_blank" rel="noopener noreferrer">https://store.devcore.cz/</a>, the script can be downloaded here <a href="https://keymaster.fivem.net/assets" target="_blank" rel="noopener noreferrer">https://keymaster.fivem.net/assets</a></p>
          <p class="mb-1">2. Upload the contents of the zip file to your server</p>
          <p class="mb-1">3. Ensure devcore_tags in server.cfg after your framework</p>
          <p class="mb-1">5. Edit the config as you wish and enjoy your new script. 😄</p>
        </div>
      </div>
    `,
    tagSystemExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Tag System Exports (Server-Side)</h4>
      <p class="text-gray-300 mb-4">The Tag System script includes exports for integration with other scripts. Exports to check active/inactive Tags with permission check, active/inactive Tags without permission check if you have your own check and export to deactivate Tag for player</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">Use Water Gun</h5>
        <p class="text-gray-300 mb-2">This server-side export is used to check active/inactive Tags with permission check.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_tags']:TagActiveWithCheck(source)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>source</code>: User source (id).</li>
        </ul>
      </div>
    `,
    tagSystemExports: `
      <h4 class="text-xl font-semibold mb-2 text-devcore.primary">Tag System Exports (Server-Side)</h4>
      <p class="text-gray-300 mb-4">The Tag System script includes several exports for integration with other scripts. Exports to activagte/deactivate Tag with permission check, activate/deactivate Tag without permission check if you have your own check and export to deactivate Tag for player</p>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">1. Activate/Deactive Tag with permission check</h5>
        <p class="text-gray-300 mb-2">This server-side export is used to activate/deactivate Tag with permission check.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_tags']:TagActiveWithCheck(source)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>source</code>: The source (ID) of the player.</li>
        </ul>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">2. Activate/Deactivate Tag without permission check</h5>
        <p class="text-gray-300 mb-2">This server-side export is used to activate/deactivate Tag without permission check (use this if you have your own check).</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_tags']:TagActiveWithoutCheck(source, group)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>source</code>: The source (ID) of the player.</li>
          <li><code>group</code>: The group to use the tag for (e.g. 'admin').</li>
        </ul>
      </div>

      <div class="mb-6">
        <h5 class="text-lg font-semibold mb-2 text-devcore.primary">3. Deactivate Tag</h5>
        <p class="text-gray-300 mb-2">This server-side export is used to just deactivate Tag.</p>
        <div class="bg-gray-950/20 border border-gray-600/50 rounded-lg mb-1 flex items-center gap-3">
        <div class="bg-devcore.dark p-4 rounded-lg mb-2">
          <div class="flex space-x-2 mb-3">
            <div class="w-3 h-3 rounded-full bg-red-500"></div>
            <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div class="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <pre class="font-mono text-sm text-gray-300 overflow-x-auto">
            <code>
exports['devcore_tags']:TagDeactive(source)
            </code>
          </pre>
        </div>
        </div>
        <p class="text-gray-300 mb-1"><b>Parameters:</b></p>
        <ul class="list-disc list-inside text-gray-300 ml-4 mb-2">
          <li><code>source</code>: The source (ID) of the player.</li>
        </ul>
      </div>
    `,
  };

  return (
    <section id="docs" className="py-20 px-6 bg-devcore.dark pt-32">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprehensive <span className="gradient-text">Documentation</span></h2>
          <p className="text-gray-300 max-w-2xl mx-auto">Everything you need to implement and customize our scripts</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
<div className="lg:w-1/4">
  <div className="glass-card rounded-xl p-4 sticky top-24 max-h-[80vh] overflow-y-auto docs-sidebar">
    <h3 className="font-semibold text-lg mb-4 text-devcore.primary">Table of Contents</h3>

    <div className="space-y-2">
      <div>
        <button
          onClick={() => setCurrentContentKey('gettingStarted')}
          className={`w-full flex justify-between items-center py-2 px-3 rounded-lg transition text-white ${currentContentKey === 'gettingStarted' ? 'bg-white bg-opacity-10' : 'hover:bg-white hover:bg-opacity-10'}`}
        >
          <span>Getting Started</span>
        </button>
      </div>

      {docsData.map((categoryData, catIndex) => (
        <div key={catIndex}>
          <button
            onClick={() => toggleCategory(categoryData.category)}
            className="w-full flex justify-between items-center py-2 px-3 rounded-lg hover:bg-white hover:bg-opacity-10 transition text-white"
          >
            <span>{categoryData.category}</span>
            <i className={`fas fa-chevron-${openCategories[categoryData.category] ? 'up' : 'down'} text-xs transition-transform`}></i>
          </button>
          
          {openCategories[categoryData.category] && (
            <div className="pl-4 mt-1 space-y-1">
              {categoryData.items.map((item, itemIndex) => (
                <div key={itemIndex}>
                  {item.subsections ? (
                    <>
                      <button
                        onClick={() => toggleCategory(`${categoryData.category}-${item.name}`)}
                        className="w-full flex justify-between items-center py-1 px-3 rounded-lg hover:bg-white hover:bg-opacity-10 transition text-left text-gray-200"
                      >
                        <span>{item.name}</span>
                        <i className={`fas fa-chevron-${openCategories[`${categoryData.category}-${item.name}`] ? 'up' : 'down'} text-xs transition-transform`}></i>
                      </button>
                      
                      {openCategories[`${categoryData.category}-${item.name}`] && (
                        <div className="pl-4 mt-1 space-y-1">
                          {item.subsections.map((sub, subIndex) => (
                            <a
                              key={subIndex}
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentContentKey(sub.contentKey);
                              }}
                              className={`block py-1 px-3 rounded transition text-gray-300 ${currentContentKey === sub.contentKey ? 'bg-white bg-opacity-5' : 'hover:bg-white hover:bg-opacity-5'}`}
                            >
                              {sub.title}
                            </a>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-1 px-3 rounded hover:bg-white hover:bg-opacity-5 transition text-gray-300"
                    >
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
</div>

          <div className="lg:w-3/4">
            <div className="glass-card rounded-xl p-8">
              {currentContentKey === 'realisticSmokingConfigs'? (
                <RealisticSmokingConfigsContent
                  configCode={`Config = {}
-- DISCORD : https://discord.gg/zcG9KQj3sa
-- STORE : https://store.devcore.cz/


-- Framework is automatically detected here you have the option to replace the default exports with your own
Config.Export = function()
    if Framework == 'ESX'  then -- ESX
-- insert your esx export here
    return exports['es_extended']:getSharedObject()
--
    elseif Framework == 'QB'  then -- QB or QBX 
-- insert your qb or qbx export here
    return exports['qb-core']:GetCoreObject()
--
    end
end


Config.Settings = {
    OptionsMenuPosition = 'top-right', -- Position for options menu
    OxDurabilitySystem = true, -- Durability for lighters true for enable (only for ox_inventory)
    TakeEarItemCommand = 'takecig', -- An order to remove a stored smoking item in a position behind the ear..
    DisableCombatButtons = true, -- true if you want disable combat and aim buttons
    SoundEffects = true, -- only with xsound
    
    RollingJointsMinigame = { -- minigame from ox_lib
        Enable = true, -- true if you want to have a mini-game before packing your joints
        Difficulty = 'easy', -- easy / medium / hard
        areaSize = 50,
        speedMultiplier = 1,
        Buttons = {'a', 'd'}
    },
}


Config.HighSystem = { -- High Effect Settings
    MaxHigh = 1.6, -- Max high value 
    Effect = 'spectator9', -- High effect up to size 1.0
    ShakeCamWhileAiming = true, -- Cam vibration during aiming at high effect above 1.0
    DisableSprint = true, -- disable sprint when high effect is above 1.0
    RemoveHigh = {
        Remove = 0.04, -- Set a rank that is removed every 15 seconds (this makes the effect go away more slowly)
        Time = 15, -- Every 15 seconds 0.04 values are taken from the high effect
        RemoveEffect = 'DrugsDrivingOut', -- The name of the effect that is executed when the high effect is above 1.0, each time the effect is reduced, false for disable. https://forge.plebmasters.de/animpostfx
    },
    HighMovement = { -- Movement will change when Hight value exceeds size 1.0
        enable = true,
        anim = 'move_m@drunk@slightlydrunk',
    }
}


Config.Buttons = { 
    Use = 176, -- LEFT CLICK ON MOUSE
    Accept = 38, -- E 
    Light = 38, -- E 
    Charge = 10, -- PAGEUP
    Options = 310, -- R 
    Throw = 73, -- X 
    Back = 73, -- X 
    Hide = 73, -- X
}


-- STATUS --
Config.Status = { --Settings for adding Armor, remove Stress and High Effect
    ["ARMOR"] = function(data) -- Trigger to increase thirst status
        AddArmourToPed(PlayerPedId(), data.AfterExhale.Status['ARMOR'])
    end,
    ["STRESS"] = function(data) -- Here you can insert an export or trigger for your stress system
        if Framework == 'ESX' then

         TriggerEvent('esx_status:remove', 'stress', data.AfterExhale.Status['STRESS']) -- this trigger only works for the esx_status stress system

        elseif Framework == 'QB' then -- Trigger for QB-Core / QBX

            TriggerServerEvent('hud:server:RelieveStress', data.AfterExhale.Status['STRESS'])
            
        end
    end,
    ["HIGH"] = function(data) -- This is a default function that does not need to be modified
        SetHigh(data.AfterExhale.Status['HIGH']) -- Default function from devcore_smoking
    end,
}

-- NOTIFY FUNCTION --
Config.Notify = function(message, type)
    if Framework == 'ESX' then
        -- For your notify remove ESX.ShowNotification(message, 2000, type) and put your export or trigger 
        ESX.ShowNotification(message, 2000, type) -- Default ESX Notify

    elseif Framework == 'QB' then
        -- For your notify remove QBCore.Functions.Notify(message, type) and put your export or trigger 
        QBCore.Functions.Notify(message, type) -- Default QB Notify

    end
end


--CUSTOM DRAW MENU FUNCTION-- here put your export or trigger for your menu and delete - DrawSmokeMenu(text, menuid)
Config.CustomMenu = function(text, menuid) -- menuid only for default realistic smoking draw menu.
    DrawSmokeMenu(text, menuid) 
end


-- MENU CUSTOMIZE --
Config.Menu = {

    ['MAIN'] = { -- MAIN DRAW MENU
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.50, y = 0.95},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
    ['STATUS'] = { -- STATUS DRAW MENU
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.984, y = 0.90},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
    ['OPTIONS'] = { -- OPTIONS DRAW MENU
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.972, y = 0.94},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },

}


-- MENU TEXT AND TEXT --
Translations = {
    ['MENU'] = {
        ['USE'] =  {tag = '~g~[M1]~w~ ', label = 'USE'},
        ['OPTIONS'] = {tag = '~g~[R]~w~ ', label = 'OPTIONS'},
        ['LIGHT'] = {tag = '~g~[E]~w~ ', label = 'LIGHT'},
        ['CHARGE'] = {tag = '~g~[PGUP]~w~ ', label = 'CHARGE'},
        ['HIDE'] = {tag = '~g~[X]~w~ ', label = 'HIDE'},
        ['THROW'] = {tag = '~g~[X]~w~ ', label = 'THROW'},
        ['BACK'] = {tag = '~g~[X]~w~ ', label = 'BACK'},
        ['ACCEPT'] = {tag = '~g~[E]~w~ ', label = 'ACCEPT'},
    },
    ['TEXT'] = {
        ['charge_menu_title'] = 'Select an item',
        ['charge_desc'] = 'Click to charge: ',
        ['cigarette_pack_menu_title'] = 'How many cigarettes do you want to take out of the pack?',
        ['cigarette_pack_menu_desc'] = 'Max: ',
        ['cigarette_pack_menu_label'] = 'Cigarette item in pack: ',
        ['papers_quantity'] = 'Quantity of papers',
        ['roll_joints_desc'] = 'Click to start rolling: ',
        ['roll_joints_starting'] = 'Starting rolling joint',
        ['failed_roll'] = 'You failed to roll a joint',
        ['success_roll'] = 'He successfully rolled ',
        ['no_near_you'] = 'Theres no one near you',
        ['dont_have_item'] = 'You dont carry the necessary items to fill',
        ['hidden_menu_info'] = 'You have a hidden menu',
        ['max_high'] = 'Youre already too high. Wait a minute.',
        ['dont_have_item_for_rolling'] = 'You dont have the right item for packing joints ',
        ['already'] = 'Youre already smoking',
        ['player_already'] = 'Player already smoking',
        ['save_position'] = 'To take the item back into your hand, use the command /' ..Config.Settings.TakeEarItemCommand,
        ['not_filled'] = 'You have to fill the bong first',
        ['not_lighter'] = 'You dont have Lighter',
        ['options_menu_title'] = 'Smoking Options',
        ['options_positions_label'] = 'Positions',
        ['options_hide_menu_label'] = 'Menu show/hide',
        ['options_hide_label'] = 'Hide',
        ['options_filled_label'] = 'Filled',
        ['options_show_label'] = 'Show',
        ['options_give_label'] = 'Give',
        ['options_mouth_positions'] = 'Mouth',
        ['options_hand_positions'] = 'Hand',
        ['options_ear_positions']= 'Ear',
        ['status_bong'] = 'g', -- for ox_inventory metadata description
        ['status_vape'] = 'ml', -- for ox_inventory metadata description
        ['status_puff_vape'] = ' PUFFS', -- for ox_inventory metadata description
        ['empty_lighter'] = 'Lighter is empty',
    },
}`}
                  itemsCode={`

---- ROLLING PAPERS ITEMS ----
Config.RollingPapers = { -- Any amount of rolling papers can be added
    --
    ['ocb_paper'] = { -- ITEM NAME
        Quantity = 30, -- Number of pieces of paper in the package 
        Prop = {Paper = 'p_cs_papers_02', Weed = 'bkr_prop_weed_bud_pruned_01a'},
        Positions = { -- Post
                ['PAPER'] = {
                    BoneID = 18905,
                    Offset = vector3(0.12, 0.0, 0.02),
                    Rot = vector3(180.0, -145.0, 120.0),},
                ['WEED'] = {
                    BoneID = 57005,
                    Offset = vector3(0.13, 0.07, -0.03),
                    Rot = vector3(-89.0, 192.0, -6.0),},
                ['ROLL_ITEM'] = { -- Rolling item prop positions
                    BoneID = 64097,
                    Offset = vector3(0.020, 0.0, 0.04),
                    Rot = vector3(90.0, 0.0, -198.0),},
        },
        Anims = {
            ['ROLL'] = { -- Animation rolling joints
                [1] = {anim = 'clip_rolling', dict = 'foxyando@rolling', params = {1.0, -1.0, 14000, 49, 1}},
            },
        },
        RollingItems = {  -- Here you set the names of the items from which it will be possible to rolling the joint and also specify the names of the items for the joints
            ['og_kush_weed'] = { joint = 'og_kush_joint'}, 
            ['banana_kush_weed'] = { joint = 'banana_kush_joint'},
            ['purple_haze_weed'] = { joint = 'purple_haze_joint'},
            ['blue_dream_weed'] = { joint = 'blue_dream_joint'},
           -- ['example_weed'] = { joint = 'example_joint'},
        }
    },

    --
    
    ['raw_paper'] = { -- ITEM NAME
        Quantity = 35, -- Number of pieces of paper in the package 
        Prop = {Paper = 'p_cs_papers_02', Weed = 'bkr_prop_weed_bud_pruned_01a'},
        Positions = {
                ['PAPER'] = { 
                    BoneID = 18905,
                    Offset = vector3(0.12, 0.0, 0.02),
                    Rot = vector3(180.0, -145.0, 120.0),},
                ['WEED'] = {
                    BoneID = 57005,
                    Offset = vector3(0.13, 0.07, -0.03),
                    Rot = vector3(-89.0, 192.0, -6.0),},
                ['ROLL_ITEM'] = {
                    BoneID = 64097,
                    Offset = vector3(0.020, 0.0, 0.04),
                    Rot = vector3(90.0, 0.0, -198.0),},
        },
        Anims = {
            ['ROLL'] = { -- Animation rolling joints
                [1] = {anim = 'clip_rolling', dict = 'foxyando@rolling', params = {1.0, -1.0, 14000, 49, 1}},
            },
        },
        RollingItems = { -- Here you set the names of the items from which it will be possible to rolling the joint and also specify the names of the items for the joints
            ['og_kush_weed'] = { joint = 'og_kush_joint'},
            ['banana_kush_weed'] = { joint = 'banana_kush_joint'},
            ['purple_haze_weed'] = { joint = 'purple_haze_joint'},
            ['blue_dream_weed'] = { joint = 'blue_dream_joint'},
             -- ['example_weed'] = { joint = 'example_joint'},
        }
    },
    --

}

---- LIGHTERS ITEMS ----

Config.Lighters = {-- Any amount of Lighters can be added

    --
    ['classic_lighter'] = { -- ITEM NAME
        Prop = {Default = 'ex_prop_exec_lighter_01'},
        Remove_Durability = {min = 1, max = 5}, -- Only for ox_inventory
        Positions = { -- 
            ['DEFAULT'] = { -- Lighter position if you are lighting an item with Type NEED_LIGHTER 
                BoneID = 57005,
                Offset = vector3(0.14, 0.01, 0.0),
                Rot = vector3(-89.0, 192.0, -6.0),},
            ['BONG'] = { --Lighter position if you are lighting an item with Type BONG 
                BoneID = 18905,
                Offset = vector3(0.12, 0.01, 0.02),
                Rot = vector3(-119.0, 36.0, -32.0),},
        },
        Particles = {
            ['Light'] = {-- light effect 
                particleName = 'ent_amb_torch_fire', -- ent_amb_fire_ring
                particleDict = 'core',
                Scale = 0.03,
                Offset = vector3(0.0, 0.0, 0.050),
                Rot = vector3(0.0, 0.0, 0.0),},
        },
        Anims = {
            ['LIGHT'] = { -- Animation Lighting a Cigarette
                [1] = {anim = 'devcore_light_c_1', dict = 'lightcig_1@devcore', params = {1.0, -1.0, 3000, 49, 1}, sound = {url = './sounds/devcore_light_1.mp3', volume = 0.3, distance = 5}},
                [2] = {anim = 'devcore_light_c_2', dict = 'lightcig_2@devcore', params = {1.0, -1.0, 3000, 49, 1}, sound = {url = './sounds/devcore_light_1.mp3', volume = 0.3, distance = 5}},
                [3] = {anim = 'devcore_light_c', dict = 'lightcig@devcore', params = {1.0, -1.0, 3000, 49, 1}, sound = {url = './sounds/devcore_light_1.mp3', volume = 0.3, distance = 5}},},
            ['TAKEOUT'] = { -- Animation of putting the cigarette in the pocket and taking it out of the pocket
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},},
        },
    }, 

    ['gold_lighter'] = { -- ITEM NAME
        Prop = {Default = 'lux_prop_lighter_luxe'},
        Remove_Durability = {min = 1, max = 5}, -- Only for ox_inventory
        Positions = {
            ['DEFAULT'] = { -- Lighter position if you are lighting an item with Type NEED_LIGHTER 
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.0),
                Rot = vector3(-89.0, 192.0, -6.0),},
            ['BONG'] = { -- Lighter position if you are lighting an item with Type BONG 
                BoneID = 18905,
                Offset = vector3(0.12, 0.01, 0.02),
                Rot = vector3(-119.0, 36.0, -32.0),},
        },
        Particles = {
            ['Light'] = {-- light effect 
                particleName = 'ent_amb_torch_fire',
                particleDict = 'core',
                Scale = 0.03,
                Offset = vector3(0.0, 0.0, 0.050),
                Rot = vector3(0.0, 0.0, 0.0),},
        },
        Anims = {
            ['LIGHT'] = { -- Animation Lighting a Cigarette
                [1] = {anim = 'devcore_light_c', dict = 'lightcig@devcore', params = {1.0, -1.0, 3000, 49, 1}, sound = {url = './sounds/devcore_light_1.mp3', volume = 0.5, distance = 10}},
                [2] = {anim = 'devcore_light_c_2', dict = 'lightcig_2@devcore', params = {1.0, -1.0, 3000, 49, 1}, sound = {url = './sounds/devcore_light_1.mp3', volume = 0.5, distance = 10}},},
            ['TAKEOUT'] = {-- Animation of putting the cigarette in the pocket and taking it out of the pocket
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},},
        },
    }, 
    
}


---- CIGARETTE PACKS ITEMS ----
Config.CigarettePack = {-- Any amount of Cigarette Packages can be added
--
    ['redwood_pack'] = { -- Item name 
        Prop = {Default = 'ng_proc_cigpak01a'}, -- Prop model name 
        Quantity = 20, -- Number of items in a packet of cigarettes
        ItemInPack = 'cig_redwood', -- Name of the item that will be in the cigarette pack
        Positions = {  -- Prop position in hand
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.0),
                Rot = vector3(-89.0, 192.0, -6.0),
        },
        Anims = {
            ['OPEN'] = { -- Animation when taking items out of a pack of cigarettes
                [1] = {anim = 'a_uncuff', dict = 'mp_arresting', params = {1.0, -1.0, 2000, 49, 1}},},
            ['TAKEOUT'] = { -- Animation of putting the cigarette in the pocket and taking it out of the pocket
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},},
         }
    },
    -- 
    ['marlboro_pack'] = { -- Item name
        Prop = {Default = 'ng_proc_cigpak01a'}, -- Prop model name 
        Quantity = 20, -- Number of items in a packet of cigarettes
        ItemInPack = 'cig_marlboro', -- Name of the item that will be in the cigarette pack
        Positions = {  -- Prop position in hand
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.0),
                Rot = vector3(-89.0, 192.0, -6.0),
        },
        Anims = {
            ['OPEN'] = { -- Animation when taking items out of a pack of cigarettes
                [1] = {anim = 'a_uncuff', dict = 'mp_arresting', params = {1.0, -1.0, 2000, 49, 1}},},
            ['TAKEOUT'] = { -- Animation of putting the cigarette in the pocket and taking it out of the pocket
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},},
         }
    },
    -- 

}


---- SMOKING ITEMS ----

Config.SmokingItems = { -- Any amount of Smoking Items can be added

-- CIGARETTE -- TYPE : NEED_LIGHTER
    ['cig_redwood'] = { -- ITEM NAME
        Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'prop_cs_ciggy_01', Light = 'prop_cs_ciggy_01b'}, -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
        Setting = {
            Size = 100, -- The size for the NEED_LIGHTER type sets a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked.
            Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 1}, -- Smoke exhalation time
            RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size 
            ExhaleEffect = {enable = true, intensity = 60, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 0.5}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = false, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = nil, -- Adds armor after each puff of smoke
            ['STRESS'] = 1, -- Removes stress after smoke exhalation
            ['HIGH'] = nil, -- Adds a high after exhaling smoke
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 64097,
                Offset = vector3(0.020, 0.02, -0.008),
                Rot = vector3(100.0, 0.0, 100.0),},
            ['MOUTH'] = { -- Prop position in mouth 
                BoneID = 47419,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['EAR'] = { -- Prop position behind the ear
                BoneID = 12844,
                Offset = vector3(0.05, -0.03, -0.08),
                Rot = vector3(80.0, 7.0, -118.0),},
        },
        Particles = {
            ['Exhale'] = {-- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.1,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['Prop'] = {-- Smoke particles on prop 
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.02,
                Offset = vector3(-0.050, 0.0, 0.0),
                Rot = vector3(0.0, 0.0, 0.0),}
        },
        Anims = { -- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1600, 49, 1}},
            },
            ['MOUTH'] = {
                [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['EAR'] = {
                [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

    -- CIGARETTE -- TYPE : NEED_LIGHTER
    ['cig_marlboro'] = { -- ITEM NAME
        Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'prop_cs_ciggy_01', Light = 'prop_cs_ciggy_01b'}, -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
        Setting = {
            Size = 100,  --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
            Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 1}, -- Smoke exhalation time
            RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size 
            ExhaleEffect = {enable = true, intensity = 40, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = false, value = 0.0}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = false, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = nil, -- Adds armor after each puff of smoke
            ['STRESS'] = nil, -- Removes stress after smoke exhalation
            ['HIGH'] = nil, -- Adds a high after exhaling smoke
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 64097,
                Offset = vector3(0.020, 0.02, -0.008),
                Rot = vector3(100.0, 0.0, 100.0),},
            ['MOUTH'] = { -- Prop position in mouth 
                BoneID = 47419,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['EAR'] = { -- Prop position behind the ear
                BoneID = 12844,
                Offset = vector3(0.05, -0.03, -0.08),
                Rot = vector3(80.0, 7.0, -118.0),},
        },
        Particles = {
            ['Exhale'] = {-- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.1,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['Prop'] = {-- Smoke particles on prop 
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.02,
                Offset = vector3(-0.050, 0.0, 0.0),
                Rot = vector3(0.0, 0.0, 0.0),}
        },
        Anims = { -- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1600, 49, 1}},
            },
            ['MOUTH'] = {
                [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['EAR'] = {
                [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

    -- CIGARS -- TYPE : NEED_LIGHTER

    ['cigar_cuban'] = { -- ITEM NAME
        Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars) , BONG(bongs), VAPE(Must be topped up with liquid) , VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'prop_cigar_02', Light = 'prop_cigar_01'},  -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
        Setting = {
            Size = 150,  --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
            Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 2}, -- Smoke exhalation time
            RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size
            ExhaleEffect = {enable = true, intensity = 90, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 0.7}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = 5, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = nil, -- Adds armor after each puff of smoke
            ['STRESS'] = nil, -- Removes stress after smoke exhalation
            ['HIGH'] = nil, -- Adds a high after exhaling smoke 
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 64097,
                Offset = vector3(0.020, 0.02, -0.008),
                Rot = vector3(100.0, 0.0, -100.0),},
            ['MOUTH'] = { -- Prop position in mouth 
                BoneID = 47419,
                Offset = vector3(0.01, 0.0, 0.01),
                Rot = vector3(6.0, 32.0, -86.0),},
            ['EAR'] = { -- Prop position behind the ear
                BoneID = 12844,
                Offset = vector3(0.05, -0.03, -0.08),
                Rot = vector3(-1.0, 1.0, 63.0),},
        },
        Particles = {
            ['Exhale'] = {-- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.1,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['Prop'] = {-- Smoke particles on prop 
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.04,
                Offset = vector3(0.050, 0.0, 0.0),
                Rot = vector3(0.0, 0.0, 0.0),}
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1300, 49, 1}},
            },
            ['MOUTH'] = {
                [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['EAR'] = {
                [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

        -- CIGARS -- TYPE : NEED_LIGHTER

    ['cigar_davidoff'] = { -- ITEM NAME
        Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars) , BONG(bongs), VAPE(Must be topped up with liquid) , VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'prop_cigar_02', Light = 'prop_cigar_01'},  -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
        Setting = {
            Size = 100,  --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
            Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 2}, -- Smoke exhalation time
            RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size
            ExhaleEffect = {enable = true, intensity = 80, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 0.6}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = 5, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = nil, -- Adds armor after each puff of smoke
            ['STRESS'] = nil, -- Removes stress after smoke exhalation
            ['HIGH'] = nil, -- Adds a high after exhaling smoke 
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 64097,
                Offset = vector3(0.020, 0.02, -0.008),
                Rot = vector3(100.0, 0.0, -100.0),},
            ['MOUTH'] = { -- Prop position in mouth 
                BoneID = 47419,
                Offset = vector3(0.01, 0.0, 0.01),
                Rot = vector3(6.0, 32.0, -86.0),},
            ['EAR'] = { -- Prop position behind the ear
                BoneID = 12844,
                Offset = vector3(0.05, -0.03, -0.08),
                Rot = vector3(-1.0, 1.0, 63.0),},
        },
        Particles = {
            ['Exhale'] = {-- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.1,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['Prop'] = {-- Smoke particles on prop 
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.03,
                Offset = vector3(0.050, 0.0, 0.0),
                Rot = vector3(0.0, 0.0, 0.0),}
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1300, 49, 1}},
            },
            ['MOUTH'] = {
                [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['EAR'] = {
                [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

    -- JOINTS -- TYPE : NEED_LIGHTER

    ['og_kush_joint'] = { -- ITEM NAME
        Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'p_cs_joint_02', Light = 'p_cs_joint_01'}, -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
        Setting = {
            Size = 100,  --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
            Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 2}, -- Smoke exhalation time in sec
            RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size
            ExhaleEffect = {enable = true, intensity = 40, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 2.0}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = 2, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = 1, -- Adds armor after each puff of smoke
            ['STRESS'] = nil, -- Removes stress after smoke exhalation
            ['HIGH'] = 1, -- Adds a high after exhaling smoke
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand 
                BoneID = 64097,
                Offset = vector3(0.020, 0.02, -0.008),
                Rot = vector3(100.0, 0.0, 100.0),},
            ['MOUTH'] = { -- Prop position in mouth 
                BoneID = 47419,
                Offset = vector3(0.01, -0.01, 0.01),
                Rot = vector3(-2.0, 0.0, 96.0),},
            ['EAR'] = { -- Prop position behind the ear
                BoneID = 12844,
                Offset = vector3(0.05, -0.03, -0.08),
                Rot = vector3(80.0, 7.0, -118.0),},
        },
        Particles = {
            ['Exhale'] = {-- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.1,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['Prop'] = {-- Smoke particles on prop 
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.03,
                Offset = vector3(-0.050, 0.0, 0.0),
                Rot = vector3(0.0, 0.0, 0.0),}
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1300, 49, 1}},
            },
            ['MOUTH'] = {
                [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['EAR'] = {
                [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

    -- JOINTS -- TYPE : NEED_LIGHTER

    ['banana_kush_joint'] = { -- ITEM NAME
        Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'p_cs_joint_02', Light = 'p_cs_joint_01'}, -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
        Setting = {
            Size = 100,  --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
            Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 1}, -- Smoke exhalation time
            RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size
            ExhaleEffect = {enable = true, intensity = 70, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 2.5}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = 3,-- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = 2, -- Adds armor after each puff of smoke
            ['STRESS'] = nil, -- Removes stress after smoke exhalation
            ['HIGH'] = 1.3, -- Adds a high after exhaling smoke
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 64097,
                Offset = vector3(0.020, 0.02, -0.008),
                Rot = vector3(100.0, 0.0, 100.0),},
            ['MOUTH'] = { -- Prop position in mouth 
                BoneID = 47419,
                Offset = vector3(0.01, -0.01, 0.01),
                Rot = vector3(-2.0, 0.0, 96.0),},
            ['EAR'] = { -- Prop position behind the ear
                BoneID = 12844,
                Offset = vector3(0.05, -0.03, -0.08),
                Rot = vector3(80.0, 7.0, -118.0),},
        },
        Particles = {
            ['Exhale'] = { -- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.1,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
            ['Prop'] = {-- Smoke particles on prop
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.03,
                Offset = vector3(-0.050, 0.0, 0.0),
                Rot = vector3(0.0, 0.0, 0.0),}
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
                [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1300, 49, 1}},
            },
            ['MOUTH'] = {
                [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['EAR'] = {
                [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

    -- JOINTS -- TYPE : NEED_LIGHTER

    ['purple_haze_joint'] = { -- ITEM NAME
    Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
    Prop = {Default = 'p_cs_joint_02', Light = 'p_cs_joint_01'}, -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
    Setting = {
        Size = 100, --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
        Remove = 0.3, Time = 3}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
    AfterExhale = {
        ExhaleTime = {min = 1, max = 1}, -- Smoke exhalation time
        RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size
        ExhaleEffect = {enable = true, intensity = 90, effect = 'SwitchShortNeutralIn'}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
        ShakeCam = {effect = 'VIBRATE_SHAKE', value = 2.8}, -- Shake cam effect after exhale smoke for disable, effect = false
        CoughChance = 4,-- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
    Status = {
        ['ARMOR'] = 3, -- Adds armor after each puff of smoke
        ['STRESS'] = nil, -- Removes stress after smoke exhalation
        ['HIGH'] = 1.5, -- Adds a high after exhaling smoke
        }
    },
    Positions = {
        ['HAND'] = { -- Prop position in hand
            BoneID = 64097,
            Offset = vector3(0.020, 0.02, -0.008),
            Rot = vector3(100.0, 0.0, 100.0),},
        ['MOUTH'] = { -- Prop position in mouth 
            BoneID = 47419,
            Offset = vector3(0.01, -0.01, 0.01),
            Rot = vector3(-2.0, 0.0, 96.0),},
        ['EAR'] = { -- Prop position behind the ear
            BoneID = 12844,
            Offset = vector3(0.05, -0.03, -0.08),
            Rot = vector3(80.0, 7.0, -118.0),},
    },
    Particles = {
        ['Exhale'] = { -- Smoke particles after exhale
            particleName = 'exp_grd_bzgas_smoke',
            particleDict = 'core',
            Scale = 0.1,
            Offset = vector3(0.014, -0.003, 0.008),
            Rot = vector3(55.0, 0.0, 110.0),},
        ['Prop'] = {-- Smoke particles on prop
            particleName = 'exp_grd_bzgas_smoke',
            particleDict = 'core',
            Scale = 0.03,
            Offset = vector3(-0.050, 0.0, 0.0),
            Rot = vector3(0.0, 0.0, 0.0),}
    },
    Anims = {-- New animations can be added to each category and are always selected randomly
        ['USE'] = {
            [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
            [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
            [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
            [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1300, 49, 1}},
        },
        ['MOUTH'] = {
            [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
        },
        ['EAR'] = {
            [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
        },
        ['COUGH'] = {
            [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
        },
        ['THROW'] = {
            [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
        },
        ['GIVE'] = {
            [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
        },
        ['TAKEOUT'] = {
            [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
        },
    },
},

    -- JOINTS -- TYPE : NEED_LIGHTER
    ['blue_dream_joint'] = { -- ITEM NAME
    Type = 'NEED_LIGHTER', -- NEED_LIGHTER (joints, cigarette, cigars), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
    Prop = {Default = 'p_cs_joint_02', Light = 'p_cs_joint_01'}, -- Default is prop before lighting and Light is prop after lighting the cigarette (if you want to use only one prop, remove the Light prop and leave only the Default prop)
    Setting = {
        Size = 100,  --  TYPE : NEED_LIGHTER : set a value that decreases gradually after the item is lit, the larger the number you set, the longer the item can be smoked. 
        Remove = 0.3, Time = 3},  -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
    AfterExhale = { 
        ExhaleTime = {min = 1, max = 2}, -- Smoke exhalation time
        RemoveAfterExhale = 5, -- The set value is taken after each exhalation of smoke from the size
        ExhaleEffect = {enable = true, intensity = 100, effect = 'SwitchShortNeutralIn'}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
        ShakeCam = {effect = 'VIBRATE_SHAKE', value = 3.0}, -- Shake cam effect after exhale smoke for disable, effect = false
        CoughChance = 5, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
    Status = {
        ['ARMOR'] = 4, -- Adds armor after each puff of smoke
        ['STRESS'] = nil, -- Removes stress after smoke exhalation
        ['HIGH'] = 2, -- Adds a high after exhaling smoke
        }
    },
    Positions = {
        ['HAND'] = { -- Prop position in hand
            BoneID = 64097,
            Offset = vector3(0.020, 0.02, -0.008),
            Rot = vector3(100.0, 0.0, 100.0),},
        ['MOUTH'] = { -- Prop position in mouth 
            BoneID = 47419,
            Offset = vector3(0.01, -0.01, 0.01),
            Rot = vector3(-2.0, 0.0, 96.0),},
        ['EAR'] = { -- Prop position behind the ear
            BoneID = 12844,
            Offset = vector3(0.05, -0.03, -0.08),
            Rot = vector3(80.0, 7.0, -118.0),},
    },
    Particles = {
        ['Exhale'] = { -- Smoke particles after exhale
            particleName = 'exp_grd_bzgas_smoke',
            particleDict = 'core',
            Scale = 0.1,
            Offset = vector3(0.014, -0.003, 0.008),
            Rot = vector3(55.0, 0.0, 110.0),},
        ['Prop'] = {-- Smoke particles on prop
            particleName = 'exp_grd_bzgas_smoke',
            particleDict = 'core',
            Scale = 0.03,
            Offset = vector3(-0.050, 0.0, 0.0),
            Rot = vector3(0.0, 0.0, 0.0),}
    },
    Anims = {-- New animations can be added to each category and are always selected randomly
        ['USE'] = {
            [1] = {anim = 'idle_a', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
            [2] = {anim = 'idle_b', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
            [3] = {anim = 'idle_c', dict = 'amb@world_human_aa_smoke@male@idle_a', params = {1.0, -1.0, 2800, 49, 1}},
            [4] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1300, 49, 1}},
        },
        ['MOUTH'] = {
            [1] = {anim = 'take_mouth_c', dict = 'pos_mouth@devcore', params = {1.0, -1.0, 1000, 49, 1}},
        },
        ['EAR'] = {
            [1] = {anim = 'takecig_clip', dict = 'takecig@devcore', params = {1.0, -1.0, 1500, 49, 1}},
        },
        ['COUGH'] = {
            [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
        },
        ['THROW'] = {
            [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
        },
        ['GIVE'] = {
            [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
        },
        ['TAKEOUT'] = {
            [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
        },
    },
},

-- BONGS -- TYPE : BONG

    ['bong'] = { -- ITEM NAME
        Type = 'BONG', -- NEED_LIGHTER (joints, cigarette, cigar), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'prop_bong_01'}, -- For the BONG / VAPE / VAPE-PUFF type, only the Default prop can be set
        Setting = {
            Size = 1, -- TYPE : BONG : After filling the bong, the set size is added.
            Remove = nil, Time = nil}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 2, max = 2}, -- Smoke exhalation time
            RemoveAfterExhale = 1, -- The set value is taken after each exhalation of smoke from the size
            ExhaleEffect = {enable = true, intensity = 100, effect = 'SwitchShortNeutralIn'}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 2.8}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = 2, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = 80, -- Adds armor after each puff of smoke
            ['STRESS'] = nil, -- Removes stress after smoke exhalation
            ['HIGH'] = 5, -- Adds a high after exhaling smoke
            }
        },
        AllowedItems = {-- items with which to fill the bong, filling is up to the maximum size value
                ['og_kush_weed'] = { label = 'Og Kush Weed'},
                ['banana_kush_weed'] = { label = 'Banana Kush Weed'},
                ['purple_haze_weed'] = { label = 'Purple Haze Weed'},
                ['blue_dream_weed'] = { label = 'Blue Dream Weed'},
              --['example_weed'] = { label = 'Example Weed'},
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 57005,
                Offset = vector3(0.14, -0.26, -0.1),
                Rot = vector3(-72.0, 0.0, 8.0),},
        },
        Particles = {
            ['Exhale'] = { -- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.3,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),},
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'smokebong_c', dict = 'smokebong@devcore', params = {1.0, -1.0, 4000, 49, 1}, sound = {url = './sounds/devcore_bonghit_1.mp3', volume = 0.5, distance = 10}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['CHARGE'] = {
                [1] = {anim = 'a_uncuff', dict = 'mp_arresting', params = {1.0, -1.0, 2000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },

    -- VAPES -- TYPE : VAPE

    ['vape'] = { -- ITEM NAME
        Type = 'VAPE', -- NEED_LIGHTER (joints, cigarette, cigar), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'xm3_prop_xm3_vape_01a'}, -- For the BONG / VAPE / VAPE-PUFF type, only the Default prop can be set
        Setting = {
            Size = 5, -- TYPE : VAPE : After filling the vape, the set size is added.
            Remove = nil, Time = nil}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 2}, -- Smoke exhalation time
            RemoveAfterExhale = 0.2, -- The set value is taken after each exhalation of smoke from the size 
            ExhaleEffect = {enable = true, intensity = 60, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 1.5}, -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = false, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = nil, -- Adds armor after each puff of smoke
            ['STRESS'] = 1, -- Removes stress after smoke exhalation
            ['HIGH'] = nil, -- Adds a high after exhaling smoke
            }
        },
        AllowedItems = {-- items with which to fill the vape, filling is up to the maximum size value
                ['cherry_liquid'] = {label = 'Cherry liquid'},
                ['blueberry_liquid'] = {label = 'Blueberry liquid'},
            --  ['example_liquid'] = {label = 'Example liquid'},
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 57005,
                Offset = vector3(0.1, 0.04, -0.01),
                Rot = vector3( 0.0, 0.0, 0.0),},
        },
        Particles = {
            ['Exhale'] = { -- Smoke particles after exhale
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.2,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),}
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'smokevape_c', dict = 'smoke_vape@devcore', params = {1.0, -1.0, 2000, 49, 1}, sound = {url = './sounds/devcore_vape_1.mp3', volume = 0.5, distance = 5}},
            },
            ['CHARGE'] = {
                [1] = {anim = 'a_uncuff', dict = 'mp_arresting', params = {1.0, -1.0, 2000, 49, 1}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['THROW'] = {
                [1] = {anim = 'putdown_low', dict = 'pickup_object', params = {1.0, -1.0, 1000, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },


-- PUFF VAPES -- TYPE : VAPE-PUFF

    ['puff_vape'] = { -- ITEM NAME
        Type = 'VAPE-PUFF', -- NEED_LIGHTER (joints, cigarette, cigar), BONG(bongs), VAPE(Must be topped up with liquid), VAPE-PUFF(It has an adjustable maximum amount of puffs)
        Prop = {Default = 'ba_prop_battle_vape_01'}, -- For the BONG / VAPE / VAPE-PUFF type, only the Default prop can be set 
        Setting = {
            Size = 100, -- TYPE : VAPE-PUFF : Size is maximum puffs
            Remove = nil, Time = nil}, -- The size is subtracted by 0.3 every 3 seconds during smoking, depending on the setting (ONLY FOR: NEED_LIGHTER).
        AfterExhale = {
            ExhaleTime = {min = 1, max = 2},  -- Smoke exhalation time
            RemoveAfterExhale = 1, -- The set value is taken after each exhalation of smoke from the size 
            ExhaleEffect = {enable = true, intensity = 60, effect = false}, -- screen blackout effect after smoke exhalation and Animpostfx effect-- https://forge.plebmasters.de/animpostfx
            ShakeCam = {effect = 'VIBRATE_SHAKE', value = 1.5},  -- Shake cam effect after exhale smoke for disable, effect = false
            CoughChance = false, -- false if you don't want to have any chance of coughing / 1 is a 100% chance, the higher the value you set, the lower the chance will be.
        Status = {
            ['ARMOR'] = nil, -- Adds armor after each puff of smoke
            ['STRESS'] = 1, -- Removes stress after smoke exhalation
            ['HIGH'] = nil, -- Adds a high after exhaling smoke
            }
        },
        Positions = {
            ['HAND'] = { -- Prop position in hand
                BoneID = 57005,
                Offset = vector3(0.1, 0.0, -0.01),
                Rot = vector3( 190.0, 0.0, 0.0),},
        },
        Particles = {
            ['Exhale'] = { -- Smoke particles effect after exhale 
                particleName = 'exp_grd_bzgas_smoke',
                particleDict = 'core',
                Scale = 0.2,
                Offset = vector3(0.014, -0.003, 0.008),
                Rot = vector3(55.0, 0.0, 110.0),}
        },
        Anims = {-- New animations can be added to each category and are always selected randomly
            ['USE'] = {
                [1] = {anim = 'smokevape_c', dict = 'smoke_vape@devcore', params = {1.0, -1.0, 2000, 49, 1}, sound = {url = './sounds/devcore_vape_1.mp3', volume = 0.5, distance = 5}},
            },
            ['COUGH'] = {
                [1] = {anim = 'idle_cough', dict = 'timetable@gardener@smoking_joint', params = {1.0, -1.0, 2500, 49, 1}},
            },
            ['GIVE'] = {
                [1] = {anim = 'givetake1_a', dict = 'mp_common', params = {1.0, -1.0, 1800, 49, 1}},
            },
            ['TAKEOUT'] = {
                [1] = {anim = 'enter', dict = 'amb@world_human_smoking@male@male_a@enter', params = {1.0, -1.0, 1300, 49, 1}},
            },
        },
    },
--

}
`}
                />
              ) : currentContentKey === 'bankingSystemConfigs' ? (
                                <BankingSystemConfigsContent
                  configCode={`Config = {}
-- DISCORD : https://discord.gg/zcG9KQj3sa
-- STORE : https://store.devcore.cz/

Config.Locale = 'en' -- Set your locale here -  en/cs/sk/de/fr/es/it/pl
Config.UI = {
	Language = {
        defaultLanguage = "en",  -- Default language for the UI
        availableLanguages = {"en", "cs", "sk", "de", "fr", "es", "it", "pl"}  -- Supported languages
    }
}
Config.KickMessage = 'You have been kicked for attempting to abuse the banking system. If you think this is an error, please contact the server management.'

Config.OpenBanking = 'ox_target' -- ox_target: ox_target, okok_textui: okok textui, ox_lib: ox_lib textui, esx: esx textui, cd: cd draw text ui
Config.Notify = 'ox' -- okok: okok notify, ox_lib: ox notify, esx: esx notification, cd: cd notifications, mythic: mythic notifications, pnotify: pNotify

Config.debug = false

Config.TransactionLabels = {
    ['account_transfer'] = "Account Transfer to: ",
    ['account_transfer_2'] = "Transfer from Account: ",
    ['account_deposit'] = 'Deposit',
    ['account_withdraw'] = 'Withdrawal',
    ['savings_deposit'] = 'Transfer to Savings',
    ['savings_withdraw'] = 'Transfer from Savings',
    ['loan_transaction'] = 'Loan'
}

Config.Loans = {
    MaxActiveLoans = 1,
    MinAmount = 10000, -- Minimum loan amount
    MaxAmount = 100000, -- Maximum loan amount
    minTerm = 5, -- Minimum loan term in days
    maxTerm = 20, -- Maximum loan term in days
    InterestRate = 0.05, -- Interest rate for loans (5%)
    PaymentInterval = 1 * 24 * 60 * 60, -- 1 day (in seconds) for daily payments
    DefaultGracePeriod = 3, -- Grace period in days before next payment is due again if player can't pay
	MinPlaytime = 14, -- Minimum playtime in days before player can take a loan
}

Config.MoneyItem = 'cash' -- Item name for cash

Config.ATMProps = {
	'prop_atm_01',
	'prop_atm_02',
	'prop_atm_03',
	'prop_fleeca_atm',
	'v_5_b_atm1',
	'v_5_b_atm2',
	'fm_ltd_atm_sign_01'
}

Config.BankZones = {
	{ -- Legion Square
		pos = vec3(149.02, -1041.17, 29.37),
		h = 340.0,
		length = 0.8,
		width = 6.0,
		minZ = 28.37,
		maxZ = 31.07
	},

	{ -- Del Perro
		pos = vec3(-1212.92, -331.6, 37.79),
		h = 27.0,
		length = 0.8,
		width = 6.0,
		minZ = 36.79,
		maxZ = 39.49
	},

	{ -- Burton
		pos = vec3(-351.78, -50.36, 49.04),
		h = 341.0,
		length = 0.8,
		width = 6.0,
		minZ = 48.04,
		maxZ = 50.74
	},

	{ -- Hawick
		pos = vec3(313.37, -279.53, 54.17),
		h = 340.0,
		length = 0.8,
		width = 6.0,
		minZ = 53.17,
		maxZ = 55.87
	},

	{ -- Highway
		pos = vec3(-2961.91, 482.27, 15.7),
		h = 87.0,
		length = 0.8,
		width = 6.0,
		minZ = 14.7,
		maxZ = 17.4
	},

	{ -- Sandy Shores
		pos = vec3(1175.7, 2707.51, 38.09),
		h = 0.0,
		length = 0.8,
		width = 6.0,
		minZ = 37.09,
		maxZ = 39.79
	},

	{ -- Paleto Bay
		pos = vec3(-111.54, 6469.59, 31.62),
		h = 315.0,
		length = 0.8,
		width = 4.4,
		minZ = 30.62,
		maxZ = 33.12
	}
}


Config.Jobs = {
    ['ambulance'] = 'society_ambulance',
    ['police'] = 'society_police',
}

`}
                  logsConfigCode={`

SConfig = {}
SConfig.DiscordLogs = {
    Enabled = true,
    Logs = {
        ["transaction"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Transaction Log",
            Description = "A banking transaction has occurred."
        },
        ["banking_account_creation_basic"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Account Creation Log",
            Description = "A new banking account has been created."
        },
        ["banking_account_creation_savings"] ={
            Webhook = "",
            Color = 3447003,
            Title = "Savings Account Creation Log",
            Description = "A new savings banking account has been created."
        },
        ["banking_account_creation_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Account Creation Failure Log",
            Description = "An attempt to create a banking account has failed."
        },
        ["banking_card_creation"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Card Creation Log",
            Description = "A new banking card has been created."
        },
        ["banking_card_creation_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Card Creation Failure Log",
            Description = "An attempt to create a banking card has failed."
        },
        ["card_limit_change"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Card Limit Change Log",
            Description = "The limit on a banking card has been changed."
        },
        ["card_limit_change_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Card Limit Change Failure Log",
            Description = "An attempt to change the limit on a banking card has failed."
        },
        ["card_pin_change"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Card PIN Change Log",
            Description = "The PIN on a banking card has been changed."
        },
        ["card_pin_change_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Card PIN Change Failure Log",
            Description = "An attempt to change the PIN on a banking card has failed."
        },
        ["banking_loan_payment"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Loan Payment Log",
            Description = "A loan payment has been made."
        },
        ["banking_loan_fully_paid"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Loan Fully Paid Log",
            Description = "A banking loan has been fully paid off."
        },
        ["banking_loan_payment_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Loan Payment Failure Log",
            Description = "An attempt to make a loan payment has failed."
        },
        ["banking_loan_creation"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Loan Creation Log",
            Description = "A new banking loan has been created."
        },
        ["banking_loan_creation_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Loan Creation Failure Log",
            Description = "An attempt to create a banking loan has failed."
        },
        ["banking_society_withdrawal"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Withdrawal Log",
            Description = "A society withdrawal has been made."
        },
        ["banking_society_withdrawal_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Withdrawal Failure Log",
            Description = "An attempt to withdraw from a society has failed."
        },
        ["banking_society_deposit"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Deposit Log",
            Description = "A society deposit has been made."
        },
        ["banking_society_deposit_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Deposit Failure Log",
            Description = "An attempt to deposit into a society has failed."
        },
        ["banking_society_transfer"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Transfer Log",
            Description = "A society transfer has occurred."
        },
        ["banking_society_transfer_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Transfer Failure Log",
            Description = "An attempt to transfer funds within a society has failed."
        },
        ["banking_society_savings_deposit"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Savings Deposit Log",
            Description = "A deposit has been made into a society's savings account."
        },
        ["banking_society_savings_deposit_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Savings Deposit Failure Log",
            Description = "An attempt to deposit into a society's savings account has failed."
        },
        ["banking_society_savings_withdrawal"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Society Savings Withdrawal Log",
            Description = "A withdrawal has been made from a society's savings account."
        },
        ["banking_society_savings_withdrawal_fail"] = {
             Webhook = "",
            Color = 3447003,
            Title = "Society Savings Withdrawal Failure Log",
            Description = "An attempt to withdraw from a society's savings account has failed."
        },
        ["banking_savings_withdrawal"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Savings Withdrawal Log",
            Description = "A withdrawal has been made from a savings account."
        },
        ["banking_savings_withdrawal_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Savings Withdrawal Failure Log",
            Description = "An attempt to withdraw from a savings account has failed."
        },
        ["banking_savings_deposit"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Savings Deposit Log",
            Description = "A deposit has been made into a savings account."
        },
        ["banking_savings_deposit_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Savings Deposit Failure Log",
            Description = "An attempt to deposit into a savings account has failed."
        },
        ["banking_withdrawal"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Withdrawal Log",
            Description = "A withdrawal has been made from a banking account."
        },
        ["banking_withdrawal_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Withdrawal Failure Log",
            Description = "An attempt to withdraw from a banking account has failed."
        },
        ["banking_deposit"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Deposit Log",
            Description = "A deposit has been made into a banking account."
        },
        ["banking_deposit_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Deposit Failure Log",
            Description = "An attempt to deposit into a banking account has failed."
        },
        ["banking_transfer"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Transfer Log",
            Description = "A transfer has occurred between banking accounts."
        },
        ["banking_transfer_fail"] = {
            Webhook = "",
            Color = 3447003,
            Title = "Transfer Failure Log",
            Description = "An attempt to transfer funds between banking accounts has failed."
        }
    }
}

`}
                />
              ) : currentContentKey === 'realisticNeedsConfigs' ? (
                <RealisticNeedsConfigsContent
                  configCode={`Config = {}
-- DISCORD : https://discord.gg/zcG9KQj3sa
-- STORE : https://store.devcore.cz/

Config.PlaceItems = {} -- DONT TOUCH --

-- Framework is automatically detected here you have the option to replace the default exports with your own
Config.Export = function()
    if Framework == 'ESX'  then -- ESX
-- insert your esx export here
    return exports['es_extended']:getSharedObject()
--
    elseif Framework == 'QB'  then -- QB or QBX 
-- insert your qb or qbx export here
    return exports['qb-core']:GetCoreObject()
--
    end
end

-- MAIN --
Config.Settings = {
    SaveDB = true, -- Storing placed items in the database (after restarting the server/script the items remain on the ground)
    PlaceDistance = 2.0, -- Maximum distance for placing the item on the ground
    OptionsMenuPosition = 'top-right',
    DisableCombatButtons = true, -- Deactivate combat buttons when consuming
    AutoTake = { -- only for ox_inventory
        enable = false, -- true if the set items are to be used automatically if they are found in the inventory.
        items = { -- The listed items are automatically placed in your hand if they are in inventory and the item is not returned when you press the x button.
            'cola',
            'coffe',
            'sprunk',
            'burger',
            'taco',
            'hotdog',
        },
    },
}

-- BUTTONS --
Config.Buttons = {
    Use = 38, -- Button for consumption
    Place = 38, -- Button to place an item on the ground
    Hide = 105, -- Button to cancel consumption
    Accept = 38, -- Button to confirm that the item will be placed on the ground, Button to confirm the give of the item to the player 
    Take = 38, -- Button to pick up an item from the ground
    Pour = 176, -- Button for pouring drinks into glasses
    Options = 310, -- Button for open options menu
    TestYourself = 310, --Button for testing yourself with alcohol tester
    Back = 105 -- Button to return from the menu to place the item on the ground and give the item to the player
}

Config.Menu = {

    ['MAIN'] = { -- MAIN DRAW MENU
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.50, y = 0.95},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
    ['STATUS'] = { -- STATUS DRAW MENU
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.984, y = 0.90},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
    ['OPTIONS'] = { -- OPTIONS DRAW MENU
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.972, y = 0.94},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },

}

-- NOTIFY FUNCTION --
Config.Notify = function(message, type)
    if Framework == 'ESX' then
        -- For your notify remove ESX.ShowNotification(message, 2000, type) and put your export or trigger 
        ESX.ShowNotification(message, 2000, type) -- Default ESX Notify

    elseif Framework == 'QB' then
        -- For your notify remove QBCore.Functions.Notify(message, type) and put your export or trigger 
        QBCore.Functions.Notify(message, type) -- Default QB Notify

    end
end

-- MENU FUNCTION --
Config.CustomMenu = function(text, menuid) -- menuid is only for Realistic needs default menu 

    Draw2DNeedsMenu(text, menuid)

end

-- STATUS -- 
Config.Status = { --Settings for adding food, drink, alcohol and removing stress

    ["FOOD"] = function(data) -- Trigger to increase hunger status
        if Framework == 'ESX' then -- ESX TRIGGER / EXPORT
        
        TriggerEvent('esx_status:add', 'hunger', data.Consume.Status) -- Default triggers work only for esx_basicneeds and esx_status so for proper functioning don't forget to have the scripts enabled

        elseif Framework == 'QB' then -- QB/QBX TRIGGER / EXPORT

        TriggerServerEvent("consumables:server:addHunger", QBCore.Functions.GetPlayerData().metadata["hunger"] + data.Consume.Status)
        end
    end,

    ["DRINK"] = function(data) -- Trigger to increase thirst status
        if Framework == 'ESX' then-- ESX TRIGGER / EXPORT

        TriggerEvent('esx_status:add', 'thirst', data.Consume.Status) -- Default triggers work only for esx_basicneeds and esx_status so for proper functioning don't forget to have the scripts enabled

        elseif  Framework == 'QB' then -- QB/QBX TRIGGER / EXPORT

        TriggerServerEvent("consumables:server:addThirst", QBCore.Functions.GetPlayerData().metadata["thirst"] + data.Consume.Status)

        end
    end,

    ["STRESS"] = function(data) -- Trigger to increase stress status
         if Framework == 'ESX' then -- ESX TRIGGER / EXPORT

            TriggerEvent('esx_status:remove', 'stress', data.Consume.Stress)

        elseif Framework == 'QB' then -- QB/QBX TRIGGER / EXPORT

            TriggerServerEvent('hud:server:RelieveStress', data.Consume.Stress)
        end
    end,

    ["ALCOHOL"] = function(data) -- function to increase Alcohol status
        setAlcohol(data.Consume.Alcohol) 
    end,
}

-- DRUNK SYSTEM -- 
Config.DrunkSystem = {
    TesterItem = 'alcotester', -- Item name for breath test
    ResultTime = 3, -- Duration per breath test result in seconds
    Driving = true, -- Difficult driving under the influence of alcohol
    DisableSprint = true, -- No sprinting while drunk walking
    Ragdoll = true, --true if you want a drunk player to occasionally fall to the ground while walking.
    MaxDrunk = 2.3, -- blood alcohol limit
    Time = 15, -- Effect update time in seconds
    Remove = 0.04, -- Removal of alcohol from the blood after updating the effect
    ShakeCamWhileAiming = true, -- Cam vibration during aiming at Alcohol value above 1.0
    Effect = "spectator5", -- Effect of drunkenness
    VeryDrunkEffect = 'Dax_TripFreefall', -- Effect that appears when the alcohol value is higher than 1.0
    RemoveEffect = 'DrugsDrivingOut', -- The name of the effect that is executed when the drunk effect is above 1.0, each time the effect is reduced, false for disable. https://forge.plebmasters.de/animpostfx
    ShakeCamEffect = true, -- Shake Cam effect after each addition of alcohol.
    DarkEffectIntensity = 100, -- Intensity for slow dark screen effect when drinking alcoholic beverage / false for disable
    DrunkMovement = {
        enable = true,
        Phase = { -- phase for setting the player's movement animation according to the alcohol level
            [1] = {NeedAlcoholValue = 0.4, anim = 'move_m@drunk@slightlydrunk'},
            [2] = {NeedAlcoholValue = 0.7, anim = 'move_m@drunk@a'},
            [3] = {NeedAlcoholValue = 1.0, anim = 'move_m@drunk@verydrunk'},
        }
    },
    MovementTime = 3, -- -- the lower the time you give the harder it will be for the drunk driver to drive the vehicle
    RandomVehicleInteraction = { -- Drunk driving interactions
        {interaction = 10, time = 300}, --turn left and restore wheel pos
        {interaction = 11, time = 300}, --turn right and restore wheel pos
        {interaction = 10, time = 500}, --turn left and restore wheel pos
        {interaction = 11, time = 500}, --turn right and restore wheel pos
    },
}

-- MENU AND TEXT --
Translations = {
    ['MENU'] = {
        ['USE'] =  {tag = '~g~[E]~w~', label = 'USE'},
        ['OPTIONS'] = {tag = '~g~[R]~w~', label = 'OPTIONS'},
        ['TEST_YOURSELF'] = {tag = '~g~[R]~w~', label = 'TEST ON YOURSELF'},
        ['PLACE'] = {tag = '~g~[E]~w~', label = 'PLACE'},
        ['HIDE'] = {tag = '~g~[X]~w~', label = 'HIDE'},
        ['THROW'] = {tag = '~g~[X]~w~', label = 'THROW'},
        ['BACK'] = {tag = '~g~[X]~w~', label = 'BACK'},
        ['ACCEPT'] = {tag = '~g~[E]~w~', label = 'ACCEPT'},
        ['POUR'] = {tag = '~g~[M1]~w~', label = 'POUR'},
        ['TAKE'] = {tag = '~g~[E]~w~', label = 'TAKE'},
    },
    ['TEXT'] = { 
        ['already'] = 'You already have something in your hand',
        ['player_already'] = 'The player is already consuming something',
        ['max_drunk'] = 'Im drunk enough',
        ['add_content'] = 'How much add ml into ',
        ['large_number'] = 'Its not possible to pour more into a glass than will fit',
        ['dont_have_ml'] = 'Thats how many ml you dont have in the bottle',
        ['item_cannot_be_poured'] = 'This item cannot be poured into a glass',
        ['item_in_glass'] = 'There is a different type of drink in the glass than the one you want to pour',
        ['no_near_you'] = 'Theres no one near you',
        ['too_far_away'] = 'You cant put an item this far out',
        ['result'] = 'Result: ',
        ['request_rejected'] = 'The person refused the request for a breath test',
        ['request_text'] = 'Request for breath test',
        ['pour_menu_title'] = 'Choose what you want to pour into the ',
        ['waiting'] = ' Waiting..',
        ['hidden_menu'] = 'You have a hidden menu',
        ['options_menu_title'] = 'Options',
        ['options_autouse_label'] = 'Auto use',
        ['options_place_label'] = 'Place',
        ['options_give_label'] = 'Give',
        ['options_hide_menu_label'] = 'Menu show/hide',
        ['options_hide_label'] = 'Hide',
        ['options_filled_label'] = 'Filled',
        ['options_show_label'] = 'Show',
    },
}
`}
                  itemsCode={`
--------------CONSUME ITEMS---------------
-- PROPS = https://forge.plebmasters.de/
-- BONES INDEX = LEFT HAND : 18905 , RIGHT HAND : 28422
--[[
--------------------- LEFT HAND HOLD ANIM 
    Hold = {
            dict = "amb@code_human_wander_drinking@male@base",
            anim = "static",
            params = {8.0, -8, -1, 49, 0}
        },
-------------------- RIGHT HAND HOLD ANIM 
    Hold = {
            dict = "anim@heists@humane_labs@finale@keycards",
            anim = "ped_a_enter_loop",
            params = {8.0, -8, -1, 49, 0}
        },

]]


Config.ConsumeItems = {-- It is possible to add custom items

---FOOD--

    ["burger"] = {-- item name
        Size = 220, -- Item size
        Type = 'FOOD', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_cs_burger_01',
                BoneID = 18905,
                Offset = vector3(0.13, 0.05, 0.02),
                Rot = vector3(-180.0, 0.0, 0.0),
            },
            --[[Second = { --https://forum.cfx.re/t/free-animation-eat-with-fork/5068991
                Model = 'alcaprop_fork',
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.01),
                Rot = vector3(-118.0, 192.0, 24.0),
            }]]
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_inteat@burger",
                anim = "mp_player_int_eat_burger_fp",
                params = {nil, nil, 1200, 49}
            }
        },
        Consume = {
            Bite = {min = 15, max = 25}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 50000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
        },
    },

    

    ["chaser"] = {-- item name
        Size = 70,
        Type = 'FOOD', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_choc_ego',
                BoneID = 18905,
                Offset = vector3(0.13, 0.04, 0.0),
                Rot = vector3(48.0, 18.0, -133.0),
            },
            --[[Second = { --https://forum.cfx.re/t/free-animation-eat-with-fork/5068991
                Model = 'alcaprop_fork',
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.01),
                Rot = vector3(-118.0, 192.0, 24.0),
            }]]
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_inteat@burger",
                anim = "mp_player_int_eat_burger_fp",
                params = {nil, nil, 1200, 49}
            }
        },
        Consume = {
            Bite = {min = 15, max = 25}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 60000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ["meteorite"] = {-- item name
        Size = 80,
        Type = 'FOOD', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_choc_meto',
                BoneID = 18905,
                Offset = vector3(0.13, 0.05, 0.02),
                Rot = vector3(50.0, 30.0, 260.0),
            },
            --[[Second = { --https://forum.cfx.re/t/free-animation-eat-with-fork/5068991
                Model = 'alcaprop_fork',
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.01),
                Rot = vector3(-118.0, 192.0, 24.0),
            }]]
    },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_inteat@burger",
                anim = "mp_player_int_eat_burger_fp",
                params = {nil, nil, 1200, 49}
            }
        },
        Consume = {
            Bite = {min = 15, max = 25}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 60000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ["hotdog"] = {-- item name
        Size = 120,
        Type = 'FOOD', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
          Main = {
                Model = 'prop_cs_hotdog_01',
                BoneID = 18905,
                Offset = vector3(0.13, 0.05, 0.02),
                Rot = vector3(0.0, -140.0, -140.0),
            },
            --[[Second = { --https://forum.cfx.re/t/free-animation-eat-with-fork/5068991
                Model = 'alcaprop_fork',
                BoneID = 57005,
                Offset = vector3(0.14, 0.02, 0.01),
                Rot = vector3(-118.0, 192.0, 24.0),
            }]]
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_inteat@burger",
                anim = "mp_player_int_eat_burger_fp",
                params = {nil, nil, 1200, 49}
            }
        },
        Consume = {
            Bite = {min = 15, max = 25}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 60000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ["taco"] = {-- item name
        Size = 100,
        Type = 'FOOD', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
          Main = {
                Model = 'prop_taco_01',
                BoneID = 18905,
                Offset = vector3(0.13, 0.05, 0.02),
                Rot = vector3(0.0, -140.0, -140.0),
        },
        --[[Second = { --https://forum.cfx.re/t/free-animation-eat-with-fork/5068991
            Model = 'alcaprop_fork',
            BoneID = 57005,
            Offset = vector3(0.14, 0.02, 0.01),
            Rot = vector3(-118.0, 192.0, 24.0),
        }]]
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_inteat@burger",
                anim = "mp_player_int_eat_burger_fp",
                params = {nil, nil, 1200, 49}
            }
        },
        Consume = {
            Bite = {min = 15, max = 25}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 60000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
        },
    },


--DRINK


    ["raine"] = {-- item name
        Size = 500,
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'ba_prop_club_water_bottle',
                BoneID = 18905,
                Offset = vector3(0.14, -0.08, 0.05),
                Rot = vector3(251.0, -14.0, 11.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 60000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ["energy_drink"] = {-- item name
        Size = 500,
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_energy_drink',
                BoneID = 18905,
                Offset = vector3(0.14, -0.08, 0.05),
                Rot = vector3(251.0, -14.0, 11.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
                Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
                Status = 50000, -- set how much food or drink you want to add 
                Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
                Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
            },
        },


    ["sprunk"] = {-- item name
        Size = 330,
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_ld_can_01',
                BoneID = 28422,
                Offset = vector3(0.0, -0.01, -0.02),
                Rot = vector3(05.0, -10.0, 0.0),
            }
        },
        Anim = {
            Hold = {
                dict = "amb@code_human_wander_drinking@male@base",
                anim = "static",
                params = {8.0, -8, -1, 49, 0}
                },
            Use = {
                dict = "amb@code_human_wander_drinking_fat@male@idle_a",
                anim = "idle_c",
                params = {nil, nil, 2000, 49}
                }
            },
        Consume = {
                Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
                Status = 50000, -- set how much food or drink you want to add 
                Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
                Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
            },
        },

    ["coffe"] = {-- item name
        Size = 330,
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_fib_coffee',
                BoneID = 28422,
                Offset = vector3(0.0, -0.01, -0.02),
                Rot = vector3(05.0, -10.0, 0.0),
                }
            },
        Anim = {
            Hold = {
                dict = "amb@code_human_wander_drinking@male@base",
                anim = "static",
                params = {8.0, -8, -1, 49, 0}
                },
            Use = {
                dict = "amb@code_human_wander_drinking_fat@male@idle_a",
                anim = "idle_c",
                params = {nil, nil, 2000, 49}
                }
            },
        Consume = {
                Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
                Status = 40000, -- set how much food or drink you want to add 
                Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
                Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
            },
        },

    ["cola"] = {-- item name
        Size = 330,
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_ecola_can',
                BoneID = 28422,
                Offset = vector3(0.0, -0.01, -0.02),
                Rot = vector3(05.0, -10.0, 0.0),
                }
            },
        Anim = {
            Hold = {
                dict = "amb@code_human_wander_drinking@male@base",
                anim = "static",
                params = {8.0, -8, -1, 49, 0}
                },
            Use = {
                dict = "amb@code_human_wander_drinking_fat@male@idle_a",
                anim = "idle_c",
                params = {nil, nil, 2000, 49}
                }
            },
        Consume = {
                Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
                Status = 50000, -- set how much food or drink you want to add 
                Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
                Alcohol = nil -- Set the amount of alcohol the item should add after each drink.
            },
        },

    ["piswasser"] = {-- item name
        Size = 500,
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_amb_beer_bottle',
                BoneID = 18905,
                Offset = vector3(0.14, -0.08, 0.05),
                Rot = vector3(251.0, -14.0, 11.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 0.5, -- Set the amount of alcohol the item should add after each drink.
        },
    },

    ["mount_whiskey"] = {-- Item spawn name
        Size = 700, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_whiskey_bottle',
                BoneID = 18905,
                Offset = vector3(0.18, -0.19, 0.13),
                Rot = vector3(-120.0, 12.0, 6.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 3, -- Set the amount of alcohol the item should add after each drink.
        },
    },

    ["tequila"] = {-- Item spawn name
        Size = 700, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_tequila_bottle',
                BoneID = 18905,
                Offset = vector3(0.14, -0.28, 0.1),
                Rot = vector3(-74.0, 190.0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 4, -- Set the amount of alcohol the item should add after each drink.
        },
    },

    ["nogo_vodka"] = {-- Item spawn name
        Size = 700, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_vodka_bottle',
                BoneID = 18905,
                Offset = vector3(0.12, -0.28, 0.19),
                Rot = vector3(-120.0, .0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 3, -- Set the amount of alcohol the item should add after each drink.
        },
    },

    ["costa_del_perro"] = {-- Item spawn name
        Size = 500, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_wine_bot_01',
                BoneID = 18905,
                Offset = vector3(0.14, -0.32, 0.1),
                Rot = vector3(-74.0, 190.0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 2 -- Set the amount of alcohol the item should add after each drink.
        },
    },

    ["rockford_hill"] = {-- Item spawn name
        Size = 500, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_wine_bot_02',
                BoneID = 18905,
                Offset = vector3(0.14, -0.32, 0.1),
                Rot = vector3(-74.0, 190.0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 2 -- Set the amount of alcohol the item should add after each drink.
        },
    },

    
    ["vinewood_red"] = {-- Item spawn name
        Size = 500, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_wine_rose',
                BoneID = 18905,
                Offset = vector3(0.14, -0.32, 0.1),
                Rot = vector3(-74.0, 190.0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 2 -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ["vinewood_blanc"] = {-- Item spawn name
        Size = 500, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_wine_white',
                BoneID = 18905,
                Offset = vector3(0.14, -0.32, 0.1),
                Rot = vector3(-74.0, 190.0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 2 -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ["bleuterd"] = { -- Item spawn name
        Size = 700, -- 
        Type = 'DRINK', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = 'prop_champ_jer_01a',
                BoneID = 18905,
                Offset = vector3(0.14, -0.32, 0.1),
                Rot = vector3(-74.0, 190.0, 0.0),
            }
        },
        Anim = {
            Hold = nil,
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            }
        },
        Consume = {
            Bite = {min = 30, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
            Status = 40000, -- set how much food or drink you want to add 
            Stress = nil, -- How much stress do you want to remove after using the item if you don't want to use the stress system leave nil
            Alcohol = 2 -- Set the amount of alcohol the item should add after each drink.
        },
    },


    ---GLASSES


    ['whiskey_glass'] = {
        Size = 250,
        Type = 'GLASS', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = {full = 'prop_drink_whisky', null = 'prop_whiskey_01'}, -- Prop model null = prop of empty glass, full = prop of full glass
                BoneID = 28422, -- Id of the bone to which the prop is to be attached
                Offset = vector3(0.0, -0.01, -0.02),
                Rot = vector3(05.0, -10.0, 0.0),
            }
        },
        Anim = {
            Hold = {
                dict = "amb@code_human_wander_drinking@male@base",
                anim = "static",
                params = {8.0, -8, -1, 49, 0}
            },
            Use = {
                dict = "amb@code_human_wander_drinking@male@idle_a",
                anim = "idle_c",
                params = {nil, nil, 3000, 49}
            },
            Pour = { -- only for glasses 
                dict = "devcore_fox@pour",
                anim = "foxyando_pour_clip",
                params = {4.0, 4.0, 10000, 16, 0}
            }
        },
        Consume = { -- For glasses, there is no need to indicate alcohol, status, or stress because this information is taken from the beverage that is poured into the glass.
            Bite = {min = 30, max = 40}, -- Set how many ml or g are removed from the item after each drink / bite into food
        },
        AllowedItems = { -- Items that can be poured into the glass
            ['tequila'] = {label = 'Tequilya'},
            ['mount_whiskey'] = {label = 'Mount Whiskey'},
        },
    },

    ['flute_glass'] = {
        Size = 250,
        Type = 'GLASS', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = {full = 'prop_cs_champ_flute', null = 'prop_champ_flute'}, -- Prop model null = prop of empty glass, full = prop of full glass
                BoneID = 28422, -- Id of the bone to which the prop is to be attached
                Offset = vector3(0.06, 0.0, -0.18),
                Rot = vector3(05.0, -10.0, 0.0),
            }
        },
        Anim = {
            Hold = {
                dict = "amb@code_human_wander_drinking@male@base",
                anim = "static",
                params = {8.0, -8, -1, 49, 0}
            },
            Use = { 
                dict = "amb@code_human_wander_drinking@male@idle_a",
                anim = "idle_c",
                params = {nil, nil, 3000, 49}
            },
            Pour = { -- only for glasses 
                dict = "devcore_fox@pour",
                anim = "foxyando_pour_clip",
                params = {4.0, 4.0, 4000, 16, 0}
            }
        },
        Consume = { -- For glasses, there is no need to indicate alcohol, status, or stress because this information is taken from the beverage that is poured into the glass.
            Bite = {min = 25, max = 40}, -- Set how many ml or g are removed from the item after each drink / bite into food
        },
        AllowedItems = { -- Items that can be poured into the glass
            ['costa_del_perro'] = {label = 'Costa Del Perro'},
            ['vinewood_blanc'] = {label = 'Vinewood Sauvignon Blanc'},
            ['vinewood_red'] = {label = 'Vinewood Red Zinfadel'},
            ['rockford_hill'] = {label = 'Rockford Hill Reserve'},
            ['bleuterd'] = {label = 'Bleuterd Champagne'},
        },
    },

    ['wine_glass'] = {
        Size = 250, -- 
        Type = 'GLASS', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = {full = 'prop_drink_redwine', null = 'prop_sh_wine_glass'}, -- -- Prop model null = prop of empty glass, full = prop of full glass
                BoneID = 28422, -- Id of the bone to which the prop is to be attached
                Offset = vector3(0.01, 0.01, -0.1),
                Rot = vector3(05.0, -10.0, 0.0),
            }
        },
        Anim = {
            Hold = {
                dict = "amb@code_human_wander_drinking@male@base",
                anim = "static",
                params = {8.0, -8, -1, 49, 0}
            },
            Use = {
                dict = "amb@code_human_wander_drinking@male@idle_a",
                anim = "idle_c",
                params = {nil, nil, 3000, 49}
            },
            Pour = { -- only for glasses 
                dict = "devcore_fox@pour",
                anim = "foxyando_pour_clip",
                params = {4.0, 4.0, 4000, 16, 0}
            },
        },
        Consume = { -- For glasses, there is no need to indicate alcohol, status, or stress because this information is taken from the beverage that is poured into the glass.
            Bite = {min = 30, max = 40}, -- Set how many ml or g are removed from the item after each drink / bite into food
        },
        AllowedItems = { -- Items that can be poured into the glass
            ['costa_del_perro'] = {label = 'Costa Del Perro'},
            ['vinewood_blanc'] = {label = 'Vinewood Sauvignon Blanc'},
            ['vinewood_red'] = {label = 'Vinewood Red Zinfadel'},
            ['rockford_hill'] = {label = 'Rockford Hill Reserve'},
        },
    },

    ['shot_glass'] = {
        Size = 50,
        Type = 'GLASS', -- Item type 'DRINK' , 'FOOD' , 'GLASS'
        Prop = { -- Prop spawn data for idle & usage.
            Main = {
                Model = {full = 'p_cs_shot_glass_2_s', null = 'prop_sh_shot_glass'}, -- -- Prop model null = prop of empty glass, full = prop of full glass
                BoneID = 18905, -- Id of the bone to which the prop is to be attached
                Offset = vector3(0.11, 0.01, 0.03),
                Rot = vector3(-101.0, -91.0, 11.0),
            }
        },
        Anim = {
            Hold = {
                dict = "anim@heists@humane_labs@finale@keycards",
                anim = "ped_a_enter_loop",
                params = {8.0, -8, -1, 49, 0}
            },
            Use = {
                dict = "mp_player_intdrink",
                anim = "loop_bottle",
                params = {nil, nil, 2000, 49}
            },
            Pour = { -- only for glasses 
                dict = "devcore_fox@pour",
                anim = "foxyando_pour_clip",
                params = {4.0, 4.0, 4000, 16, 0}
            }
        },
        Consume = { -- For glasses, there is no need to indicate alcohol, status, or stress because this information is taken from the beverage that is poured into the glass.
            Bite = {min = 50, max = 50}, -- Set how many ml or g are removed from the item after each drink / bite into food
        },
        AllowedItems = { -- Items that can be poured into the glass
            ['nogo_vodka'] = {label = 'Nogo Vodka'},
            ['tequila'] = {label = 'Tequilya'},
            ['mount_whiskey'] = {label = 'Mount Whiskey'},
        },
    },
    
}
`}
                />
              ) : currentContentKey === 'mileageMeterConfigs' ? (
                <MileageMeterConfigsContent
                  configCode={`Config = {}

-- ESX --
Config.EsxExport = function()
    return exports['es_extended']:getSharedObject()
end

--Version : 2.0

Config.TwistMileageItem = 'twist_mil'
Config.DecrypterItem = 'mil_decrypter'

Config.Settings = {
    FireEngine = true, -- true, if you want the vehicle to catch fire when you unsuccessfully try to decrypt or reduce the mileage.
    AutomaticShowMenu = true, -- true if you want the mileage to be displayed whenever you sit in the owner's vehicle as a 2d menu / false if you want to display the mileage using the button
    VehicleWear = true, --True = if you want the vehicle's performance to decrease, if the vehicle has traveled many miles, the first power reduction is to 100 miles, then to 150 miles, and then every 100 miles.
    DirtyVehicle = true, -- True = as the performance of a vehicle with a large mileage decreases, the vehicle will also become more and more dirty
        MiniGame = {
            Difficulty = 'medium', -- easy / medium / hard
            areaSize = 50, -- size of the area in degrees.
            speedMultiplier = 2,
            Buttons = {'a', 'd', 's'}
        }
}

Config.Buttons = {  -- https://docs.fivem.net/docs/game-references/controls/
    Show = 208,
}

-- MENU CUSTOMIZE --
Config.Menu = {
    ['MAIN'] = {
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.95, y = 0.90},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
}

Config.Notify = function(style, msg)

    ESX.ShowNotification(msg, 2000, style)
    
end

------------------------
--If you don't understand much, you don't have to edit it, or you can ask in the support ticket on the discord.
function WearVehicle(vehicle, mileage)  --Vehicle wear adjustment 
        if mileage >= 600 then --600 is the mileage
           if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.1) -- this is a performance reduction of 0.1 is the lowest and 1.0 is the default
           end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 15.0) -- this is dirt on the vehicle if the vehicle has driven more than 600 miles
        end
        elseif mileage >= 500 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.3)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 15.0)
        end
        elseif mileage >= 400 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.4)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 15.0)
        end
        elseif mileage >= 300 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.5)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 14.0)
        end
        elseif mileage >= 200 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.6)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 12.0)
        end
        elseif mileage >= 150 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.8)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 7.0)
        end
        elseif mileage >= 100 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.9)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 5.0)
        end
        elseif mileage >= 50 then
            if Config.Settings.VehicleWear then
            SetVehicleCheatPowerIncrease(vehicle, 1.0)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 1.0)
        end
    end
end

--TEXT --
Translations = {
    ['TEXT'] = { 
        ['error_twist'] = 'Failed to twist mileage',
        ['error_decrypted'] = 'Failed to decrypt mileage',
        ['success_twist'] = 'We managed to twist the mileage on ',
        ['success_decrypted'] = 'Success to decrypt a twisted mileage.',
        ['never_reduced'] = 'The mileage of the vehicle has never been reduced.',
        ['no_owned'] = 'The vehicle has no owner',
        ['already'] = 'Youre not in the vehicle.',
        ['state_mileage'] = ' mil',
    },
}

`}
/>
              ) : currentContentKey === 'waterGunConfigs' ? (
                <WaterGunConfigsContent
                  configCode={`Config = {}

-- ESX / QB / QBX EXPORTS
Config.FrameworkExport = function()
    if fwType == 'esx' then
    return exports['es_extended']:getSharedObject()
    elseif fwType == 'qbcore' then
        return exports['qb-core']:GetCoreObject()
    elseif fwType == 'qbx' then
     return exports['qbx-core']:GetCoreObject()
    end
end

Config.ShowOnlyInfoMenu = false

------ BUTTONS ------
Config.Buttons = {
    Splash = 24,
    Reload = 80,
    Pump = 38,
    Hide = 73,
}

------------- MENU -------------
Config.Menu = {
    ['MAIN'] = {
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.50, y = 0.90},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
    ['PUMP'] = {
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.94, y = 0.94},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
    ['TANK'] = {
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.94, y = 0.90},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
}

------ NOTIFY ------
Config.Notify = function(message, type)
    if fwType == 'esx' then

         core.ShowNotification(message, 2000, type) -- Default ESX Notify

    elseif fwType == 'qbcore' then

         core.Functions.Notify(message, type) -- Default QB Notify

    end
end

------ WATER GUN ITEMS ------
Config.WaterGuns = {
    ['watergun'] = {
        max_size = 100,
        pumping = {min = 20, max = 30},
        reloding = {min = 25, max = 35},
        remove_pumping = {min = 2, max = 3},
        remove_water = {min = 1, max = 2},
        Anims = {
            Reload = {
                dict = 'anim@mp_snowball',
                anim = 'pickup_snowball',
                time = 1500,
            },
            Pump = {
                dict = 'missmic4',
                anim = 'michael_tux_fidget',
                time = 1500,
            }
        },
            Prop = {
                Model = 'watergun',
                BoneID = 18905,
                Offset = vector3(0.12, 0.06, -0.01),
                Rot = vector3(-114.0, -4.0, 1.0),
        },
    },
}

Translations = {
    ['MENU'] = {
        ['WATERGUN'] = '~g~[M1]~w~ USE ~g~[R]~w~ RELOAD ~g~[E]~w~ PUMP ~g~[X]~w~ HIDE',
        ['PUMP'] = 'Pump: ~o~',
        ['TANK'] = 'Tank: ~b~',
    },
    ['TEXT'] = {
        ['go_to_close_water'] = 'You have to be in the water to fill up',
        ['empty_tank'] = 'The water tank is empty. Go to the water and fill it up.',
        ['full_tank'] = 'The water tank is full.',
    }
}

`}
/>
              )  : currentContentKey === 'mileageMeterConfigs' ? (
                <MileageMeterConfigsContent
                  configCode={`Config = {}

-- ESX --
Config.EsxExport = function()
    return exports['es_extended']:getSharedObject()
end

--Version : 2.0

Config.TwistMileageItem = 'twist_mil'
Config.DecrypterItem = 'mil_decrypter'

Config.Settings = {
    FireEngine = true, -- true, if you want the vehicle to catch fire when you unsuccessfully try to decrypt or reduce the mileage.
    AutomaticShowMenu = true, -- true if you want the mileage to be displayed whenever you sit in the owner's vehicle as a 2d menu / false if you want to display the mileage using the button
    VehicleWear = true, --True = if you want the vehicle's performance to decrease, if the vehicle has traveled many miles, the first power reduction is to 100 miles, then to 150 miles, and then every 100 miles.
    DirtyVehicle = true, -- True = as the performance of a vehicle with a large mileage decreases, the vehicle will also become more and more dirty
        MiniGame = {
            Difficulty = 'medium', -- easy / medium / hard
            areaSize = 50, -- size of the area in degrees.
            speedMultiplier = 2,
            Buttons = {'a', 'd', 's'}
        }
}

Config.Buttons = {  -- https://docs.fivem.net/docs/game-references/controls/
    Show = 208,
}

-- MENU CUSTOMIZE --
Config.Menu = {
    ['MAIN'] = {
        TextFont = 4,
        TextColor = {255, 255, 255},
        Scale = 0.4,
        Position = {x = 0.95, y = 0.90},
        Background = {
            enable = true,
            color = { r = 35, g = 35, b = 35, alpha = 200 },
        },
    },
}

Config.Notify = function(style, msg)

    ESX.ShowNotification(msg, 2000, style)
    
end

------------------------
--If you don't understand much, you don't have to edit it, or you can ask in the support ticket on the discord.
function WearVehicle(vehicle, mileage)  --Vehicle wear adjustment 
        if mileage >= 600 then --600 is the mileage
           if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.1) -- this is a performance reduction of 0.1 is the lowest and 1.0 is the default
           end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 15.0) -- this is dirt on the vehicle if the vehicle has driven more than 600 miles
        end
        elseif mileage >= 500 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.3)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 15.0)
        end
        elseif mileage >= 400 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.4)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 15.0)
        end
        elseif mileage >= 300 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.5)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 14.0)
        end
        elseif mileage >= 200 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.6)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 12.0)
        end
        elseif mileage >= 150 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.8)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 7.0)
        end
        elseif mileage >= 100 then
            if Config.Settings.VehicleWear  then
            SetVehicleCheatPowerIncrease(vehicle, 0.9)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 5.0)
        end
        elseif mileage >= 50 then
            if Config.Settings.VehicleWear then
            SetVehicleCheatPowerIncrease(vehicle, 1.0)
            end
            if Config.Settings.VehiclePollution then
            SetVehicleDirtLevel(vehicle, 1.0)
        end
    end
end

--TEXT --
Translations = {
    ['TEXT'] = { 
        ['error_twist'] = 'Failed to twist mileage',
        ['error_decrypted'] = 'Failed to decrypt mileage',
        ['success_twist'] = 'We managed to twist the mileage on ',
        ['success_decrypted'] = 'Success to decrypt a twisted mileage.',
        ['never_reduced'] = 'The mileage of the vehicle has never been reduced.',
        ['no_owned'] = 'The vehicle has no owner',
        ['already'] = 'Youre not in the vehicle.',
        ['state_mileage'] = ' mil',
    },
}

`}
/>
              ) : currentContentKey === 'multijobConfigs' ? (
                <MultijobConfigsContent
                  configCode={`Config = {}

-- ============================================= --
--                   BASIC                       --
-- ============================================= --

-- Maximum number of jobs a player can have
Config.MaxJobs = 3

-- Shortcut to open the menu (e.g., 'F6', 'F7', etc.)
Config.OpenMenuKey = 'F6'

-- Command to open the menu
Config.MenuCommand = 'jobs'

-- ============================================= --
--                  NOTIFICATIONS                --
-- ============================================= --

Config.Notifications = {
    -- Notification type: 'ox_lib', 'esx', 'custom'
    Type = 'ox_lib',
    
    -- Notification title (only for ox_lib)
    Title = 'Job Manager',
    
    -- Notification display duration in ms
    Duration = 5000
}

-- ============================================= --
--                  TRANSLATIONS                 --
-- ============================================= --

Config.Translations = {
    -- UI Texts
    ['ui_title'] = 'MY JOBS',
    ['ui_count'] = '/3',
    ['btn_switch'] = 'SWITCH',
    ['btn_remove'] = 'REMOVE',
    
    -- Notifications
    ['job_switched'] = 'Job successfully switched',
    ['job_removed'] = 'Job successfully removed',
    ['job_left'] = 'Job successfully left',
    ['job_full'] = 'You don’t have a free slot for another job',
    ['job_replaced'] = 'Your oldest removable job has been replaced with the current job.',
    ['job_not_found'] = 'You don’t have this job saved',
    ['job_not_removable'] = 'This job cannot be removed',
    ['job_not_active'] = 'This is not your active job',
    ['job_cannot_leave'] = 'You cannot leave the unemployed status',
    ['job_cannot_remove'] = 'The unemployed status cannot be removed',
    ['job_cannot_remove_active'] = 'The active job cannot be removed',
    ['player_not_found'] = 'Player not found',
    ['player_full_no_removable'] = 'Player %s has no free slot for another job and no removable job.',
    ['player_full_will_replace'] = 'Player %s has full slots, but the oldest removable job will be replaced.',
    ['job_data_not_found'] = 'Job data not found',
    ['cannot_add_job'] = 'Cannot add the current job, you have no free slot or removable job.',
    ['cannot_save_job'] = 'Your current job will not be saved, you have no free slot or removable job.',
    ['invalid_data'] = 'Invalid data',
    ['job_replaced_with_current'] = 'Your oldest removable job has been replaced with the current job.',
    ['previous_job_replaced'] = 'Your previous job has been replaced with a new job.',
    ['oldest_job_replaced'] = 'The oldest removable job has been replaced with a new job.',
    ['cannot_add_no_space'] = 'Cannot add a new job, you have no free slot and no removable job.',
    ['job_replaced_previous'] = 'Your oldest removable job has been replaced with the previous job.',
    ['previous_job_not_saved'] = 'The previous job was not saved, you have no free slot or removable job.',
    
    -- Job descriptions
    ['unemployed'] = 'Unemployed',
    ['grade_unknown'] = 'Unknown',
    
    -- Info messages
    ['keybind_description'] = '(~HUD_COLOUR_RADAR_ARMOUR~Job~w~) - Open Multijob',
    
    -- Debugs
    ['debug_job_name'] = 'Debug - jobName: %s %s',
    ['debug_current_job'] = 'Debug - currentJob: %s %s',
    ['debug_leave_attempt'] = 'Player [%s] is attempting to leave job [%s]',
    ['debug_job_deleted'] = 'Job [%s] was successfully deleted for player [%s]',
    ['debug_job_not_found'] = 'Job [%s] was not found in the database for player [%s]',
}

-- ============================================= --
--                    SOUNDS                     --
-- ============================================= --

Config.Sounds = {
    -- List of sounds with their settings
    ['menu_open'] = {
        sound = "SELECT",
        set = "HUD_FRONTEND_DEFAULT_SOUNDSET"
    },
    ['menu_close'] = {
        sound = "BACK",
        set = "HUD_FRONTEND_DEFAULT_SOUNDSET"
    },
    ['job_switched'] = {
        sound = "MEDAL_UP",
        set = "HUD_MINI_GAME_SOUNDSET"
    },
    ['job_removed'] = {
        sound = "RACE_PLACED",
        set = "HUD_AWARDS"
    },
    ['error'] = {
        sound = "ERROR",
        set = "HUD_FRONTEND_DEFAULT_SOUNDSET"
    },
    ['click'] = {
        sound = "SELECT",
        set = "HUD_FRONTEND_DEFAULT_SOUNDSET"
    }
}

-- ============================================= --
--                  DEBUG MODE                   --
-- ============================================= --

Config.Debug = false

-- ============================================= --
--              UI CUSTOMIZATION                --
-- ============================================= --

Config.UI = {
    -- Menu position (in percentages)
    Position = {
        X = 50,  -- 50% from the left
        Y = 50   -- 50% from the top
    },
    
    -- Colors (RGB format)
    Colors = {
        MainColor = {61, 74, 237}  -- Default blue color
    },
    
    -- Sizes and dimensions
    Sizes = {
        MenuWidth = 400,
        MaxListHeight = 500,
        ItemPadding = 15,
        ButtonPadding = 6,
        ButtonBorderRadius = 5
    },
    
    -- Animations
    Animations = {
        FadeInDuration = 300,
        FadeOutDuration = 500,
        NotificationDuration = 3000
    }
}

`}
/>
              ) : currentContentKey === 'tagSystemConfigs' ? (
                <TagSystemConfigsContent
                  configCode={`Config = {}

-- Framework autodetect
core = nil -- DONT TOUCH
fwType = 'standalone' -- DONT TOUCH

if GetResourceState("es_extended") == "started" then
    core = exports['es_extended']:getSharedObject()
    fwType = "esx"
elseif GetResourceState("qb-core") == "started" then
    core = exports['qb-core']:GetCoreObject()
    fwType = "qbcore"
elseif GetResourceState("qbx-core") == "started" then
    core = exports['qbx-core']:GetCoreObject()
    fwType = "qbx"
end

-- NOTIFY FUNCTION --
Config.Notify = function(message, type)
    if fwType == 'esx' then
        
        -- For your notify remove ESX.ShowNotification(message, 2000, type) and put your export or trigger 
        core.ShowNotification(message, 2000, type) -- Default ESX Notify

    elseif fwType == 'qbcore' or fwType == 'qbx' then

        -- For your notify remove QBCore.Functions.Notify(message, type) and put your export or trigger 
        core.Functions.Notify(message, type) -- Default QB/QBX Notify

    elseif fwType == 'standalone' then
            -- HERE PUT YOUR NOTIFY EXPORT

    end
end

Config.EnableCommand = true
Config.TagCommand = 'tag'


--Permissions--
--For ESX using group system (xPlayer.getGroup()) or with exports
--For QBCore/QBX/Standalone using ACE permissions in server.cfg or with exports
Config.TagsList= {
     ['admin'] = { -- Permission name or group name
        TagLabel = 'ADMIN',
        ShowName = true,
        BackgroundColor = 'linear-gradient(to right, rgba(194, 117, 69, 0.8), rgba(200, 84, 12, 0.8))',
        Offset = 1.0,
     },
     ['developer'] = { -- Permission name or group name
        TagLabel = 'DEVELOPER',
        ShowName = true,
        BackgroundColor = 'linear-gradient(to right, rgba(47,103,66,0.8), rgba(92,179,125,0.8))',
        Offset = 1.0,
     },
     ['moderator'] = { -- Permission name or group name
        TagLabel = 'MODERATOR',
        ShowName = true,
        BackgroundColor = 'linear-gradient(to right, rgba(55,112,169,0.8), rgba(121,184,250,0.8)',
        Offset = 1.0,
     },
     ['owner'] = { -- Permission name or group name
        TagLabel = 'OWNER',
        ShowName = true,
        BackgroundColor = 'linear-gradient(to right, rgba(255, 184, 0, 0.8), rgba(255, 255, 0, 0.8))',
        Offset = 1.0,
     }
 -- ['example'] = { -- Permission name or group name
 --     TagLabel = 'Example',
 --     ShowName = true,
 --     BackgroundColor = 'linear-gradient(to right, rgba(194, 117, 69, 0.8), rgba(200, 84, 12, 0.8))',
 --     Offset = 1.0,
 --  }
}

Translation = {
     ['tag_no_permissions'] = 'You are not authorised to use the Tag',
     ['tag_deactivated'] = 'Tag has been deactivated',
     ['tag_activated'] = 'Tag has been activated'
}

`}
/>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: contentMap[currentContentKey] }} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DocsPage;